import { isValidAgentRunId } from '@apps/web/components/phoebe_chat/agent_run_id';
import { type ReferenceData } from '@apps/web/components/phoebe_chat/assistant_response_types';
import { type OptimisticThinkingMarker } from '@apps/web/components/phoebe_chat/chat_types';
import {
    type DynamicWidgetActionRequest,
    parseDynamicWidgetActionRequest,
} from '@apps/web/components/phoebe_chat/dynamic_widget_action';
import { parseRecommendationBatchAggregate } from '@apps/web/components/phoebe_chat/recommendation_aggregate';
import { isRecord } from '@apps/web/components/phoebe_chat/record_utils';
import {
    getSlackUserMentionsFromEvent,
    type SlackUserMentionData,
} from '@apps/web/components/phoebe_chat/slack_mentions';
import {
    type LiveRunStatus,
    type PhoebeAgentRunEventRead,
    type PhoebeAgentRunPendingEventRead,
} from '@apps/web/components/phoebe_chat/streaming_types';
import {
    type ActivityEntry,
    type DebugEventEntry,
    type MessageArtifactRef,
    type MessageDestinationLink,
    type MessageEntry as PlainMessageEntry,
    type MessageImageAttachment,
    type MessageUploadAttachment,
    type RecommendationBatchEntry,
    type ToolApprovalGroup,
    type ToolApprovalGroupChange,
    type ToolApprovalGroupTarget,
    type ToolApprovalPresentation,
    type ToolExecutionEntry,
    type ToolExecutionLifecycleStatus,
    type TranscriptEntry as PlainTranscriptEntry,
} from '@apps/web/components/phoebe_chat/transcript_types';
import { type AgentItemRead } from '@libraries/typescript/api_client';

type ToolAwareItem = AgentItemRead & {
    readonly tool_name?: string | null;
    readonly tool_status?: string | null;
    readonly arguments_json?: Record<string, unknown> | Array<unknown> | null;
    readonly output_json?: unknown;
    readonly raw_json?: unknown;
    readonly replaces_item_ids?: Array<string> | null;
};

type EventMessageExtension = {
    readonly widgetAction?: DynamicWidgetActionRequest;
};
type MessageEntry = PlainMessageEntry<EventMessageExtension>;
type TranscriptEntry = PlainTranscriptEntry<EventMessageExtension>;

function parseStringArray(value: unknown): Array<string> | null {
    if (!Array.isArray(value)) {
        return null;
    }
    const strings = value.filter(
        (item): item is string =>
            typeof item === 'string' && item.trim().length > 0,
    );
    return strings.length === value.length ? strings : null;
}

function hasOnlyKeys(
    value: Record<string, unknown>,
    expectedKeys: ReadonlyArray<string>,
): boolean {
    const expected = new Set(expectedKeys);
    return Object.keys(value).every((key) => expected.has(key));
}

function parseToolApprovalPresentation(
    value: unknown,
): ToolApprovalPresentation | null {
    if (!isRecord(value) || !hasOnlyKeys(value, ['question', 'groups'])) {
        return null;
    }
    const question = value['question'];
    const groupsValue = value['groups'];
    if (
        typeof question !== 'string' ||
        !question.trim() ||
        !Array.isArray(groupsValue)
    ) {
        return null;
    }
    if (question.trim() === 'Allow this action?' && groupsValue.length === 0) {
        return null;
    }

    const groups = groupsValue.map((group) => {
        if (
            !isRecord(group) ||
            !hasOnlyKeys(group, ['heading', 'items']) ||
            typeof group['heading'] !== 'string' ||
            !group['heading'].trim()
        ) {
            return null;
        }
        const items = parseStringArray(group['items']);
        if (items === null || items.length === 0) {
            return null;
        }
        return { heading: group['heading'], items };
    });
    if (groups.some((group) => group === null)) {
        return null;
    }
    return {
        question,
        groups: groups.filter((group) => group !== null),
    };
}

function parseToolDisplayArguments(
    payload: Record<string, unknown>,
): Record<string, unknown> | Array<unknown> | null {
    const argumentsJson = payload['arguments_json'];
    const automationDisplay = payload['automation_display_json'];
    if (isRecord(argumentsJson) && isRecord(automationDisplay)) {
        return { ...argumentsJson, ...automationDisplay };
    }
    const displayArguments = payload['display_arguments_json'];
    return isRecord(displayArguments) || Array.isArray(displayArguments)
        ? displayArguments
        : null;
}

function parseToolApprovalGroupChange(
    value: unknown,
): ToolApprovalGroupChange | null {
    if (!isRecord(value)) {
        return null;
    }
    if (typeof value['key'] !== 'string' || !value['key'].trim()) {
        return null;
    }
    return {
        key: value['key'],
        before: value['before'],
        after: value['after'],
        changed: value['changed'] === true,
    };
}

function parseToolApprovalGroupTarget(
    value: unknown,
): ToolApprovalGroupTarget | null {
    if (!isRecord(value)) {
        return null;
    }
    const changesValue = value['changes'];
    if (!Array.isArray(changesValue)) {
        return null;
    }
    const changes = changesValue
        .map(parseToolApprovalGroupChange)
        .filter((change): change is ToolApprovalGroupChange => change !== null);
    if (changes.length !== changesValue.length) {
        return null;
    }

    const commandId = value['command_id'];
    const callId = value['call_id'];
    const organizationId = value['organization_id'];
    const displayName = value['display_name'];
    if (
        typeof commandId !== 'string' ||
        typeof callId !== 'string' ||
        typeof organizationId !== 'string' ||
        typeof displayName !== 'string'
    ) {
        return null;
    }

    return {
        commandId,
        callId,
        organizationId,
        organizationName:
            typeof value['organization_name'] === 'string'
                ? value['organization_name']
                : null,
        organizationInternalName:
            typeof value['organization_internal_name'] === 'string'
                ? value['organization_internal_name']
                : null,
        displayName,
        changes,
    };
}

function parseToolApprovalGroup(value: unknown): ToolApprovalGroup | null {
    if (!isRecord(value)) {
        return null;
    }
    const callIds = parseStringArray(value['call_ids']);
    const commandIds = parseStringArray(value['command_ids']);
    const targetsValue = value['targets'];
    if (!callIds || !commandIds || !Array.isArray(targetsValue)) {
        return null;
    }
    const targets = targetsValue
        .map(parseToolApprovalGroupTarget)
        .filter((target): target is ToolApprovalGroupTarget => target !== null);
    if (targets.length !== targetsValue.length) {
        return null;
    }
    const featureValuesValue = value['feature_values'];
    if (!isRecord(featureValuesValue)) {
        return null;
    }
    const featureValues: Record<string, boolean> = {};
    for (const [key, featureValue] of Object.entries(featureValuesValue)) {
        if (typeof featureValue !== 'boolean') {
            return null;
        }
        featureValues[key] = featureValue;
    }

    const id = value['id'];
    const kind = value['kind'];
    const toolName = value['tool_name'];
    const title = value['title'];
    const mode = value['mode'];
    const targetCount = value['target_count'];
    const riskSummary = value['risk_summary'];
    if (
        typeof id !== 'string' ||
        typeof kind !== 'string' ||
        typeof toolName !== 'string' ||
        typeof title !== 'string' ||
        typeof mode !== 'string' ||
        typeof targetCount !== 'number' ||
        typeof riskSummary !== 'string'
    ) {
        return null;
    }

    return {
        id,
        kind,
        toolName,
        title,
        mode,
        targetCount,
        callIds,
        commandIds,
        featureValues,
        targets,
        riskSummary,
    };
}

function isReferenceData(value: unknown): value is ReferenceData {
    if (!isRecord(value)) {
        return false;
    }
    return (
        typeof value['type'] === 'string' &&
        value['type'].trim().length > 0 &&
        typeof value['id'] === 'string' &&
        value['id'].trim().length > 0 &&
        typeof value['display_name'] === 'string' &&
        value['display_name'].trim().length > 0 &&
        (value['metadata'] === undefined ||
            value['metadata'] === null ||
            isRecord(value['metadata']))
    );
}

function parseReferenceDataArray(
    value: unknown,
): Array<ReferenceData> | undefined {
    if (!Array.isArray(value)) {
        return undefined;
    }
    const references = value.filter(isReferenceData);
    return references.length > 0 ? references : undefined;
}

// Parse the `uploads` array on a UserMessageEvent payload into card-
// ready `MessageUploadAttachment` records. Silent skips (bad shape,
// missing fields) are safer than a hard throw: the message text still
// renders, and the transcript stays functional even if one attachment
// is malformed. The upload id is the durable
// `phoebe_agent_run_chat_attachments.id`; the download endpoint keys
// off that plus the runId.
function parseMessageUploadAttachments(
    value: unknown,
): Array<MessageUploadAttachment> | undefined {
    if (!Array.isArray(value)) {
        return undefined;
    }
    const uploads = value
        .filter(isRecord)
        .map((item): MessageUploadAttachment | null => {
            if (
                typeof item['upload_id'] !== 'string' ||
                typeof item['phoebe_agent_run_id'] !== 'string' ||
                typeof item['filename'] !== 'string' ||
                typeof item['content_type'] !== 'string' ||
                typeof item['size_bytes'] !== 'number'
            ) {
                return null;
            }
            return {
                attachmentId: item['upload_id'],
                runId: item['phoebe_agent_run_id'],
                filename: item['filename'],
                contentType: item['content_type'],
                disposition:
                    item['disposition'] === 'inline' ? 'inline' : 'attachment',
                sizeBytes: item['size_bytes'],
            };
        })
        .filter((upload): upload is MessageUploadAttachment => upload !== null);
    return uploads.length > 0 ? uploads : undefined;
}

// Placeholder shown in the message bubble when the coordinator sent
// only attachments (no accompanying text). Bubble content cannot be
// empty or React collapses the row, so an empty content plus visible
// attachments gets a short label describing what was sent.
function _defaultUserMessageContent({
    imageAttachments,
    uploadAttachments,
}: {
    readonly imageAttachments?: Array<MessageImageAttachment>;
    readonly uploadAttachments?: Array<MessageUploadAttachment>;
}): string {
    if (uploadAttachments && uploadAttachments.length > 0) {
        return uploadAttachments.length === 1
            ? 'Attached file'
            : `Attached ${uploadAttachments.length} files`;
    }
    if (imageAttachments && imageAttachments.length > 0) {
        return 'Uploaded image';
    }
    return '';
}

function parseMessageImageAttachments(
    value: unknown,
): Array<MessageImageAttachment> | undefined {
    if (!Array.isArray(value)) {
        return undefined;
    }
    const images = value
        .filter(isRecord)
        .map((item): MessageImageAttachment | null => {
            if (
                typeof item['artifact_id'] !== 'string' ||
                typeof item['run_id'] !== 'string' ||
                typeof item['mime_type'] !== 'string' ||
                typeof item['size_bytes'] !== 'number'
            ) {
                return null;
            }
            return {
                artifactId: item['artifact_id'],
                runId: item['run_id'],
                mimeType: item['mime_type'],
                filename:
                    typeof item['filename'] === 'string'
                        ? item['filename']
                        : null,
                sizeBytes: item['size_bytes'],
                width: typeof item['width'] === 'number' ? item['width'] : null,
                height:
                    typeof item['height'] === 'number' ? item['height'] : null,
            };
        })
        .filter((image): image is MessageImageAttachment => image !== null);
    return images.length > 0 ? images : undefined;
}

function parsePendingDeliveryMode(
    event: TranscriptBuildEvent,
): 'queue' | 'steer' | undefined {
    if (!event.id.startsWith('pending-')) {
        return undefined;
    }
    const deliveryMode = event.payload['delivery_mode'];
    if (deliveryMode === 'queue' || deliveryMode === 'steer') {
        return deliveryMode;
    }
    if (event.type === 'slack_thread_message') {
        return 'queue';
    }
    if (event.type === 'user_message') {
        return 'steer';
    }
    return undefined;
}

export function getReferencesFromRawJson(
    rawJson: unknown,
): Array<ReferenceData> | undefined {
    if (!isRecord(rawJson)) {
        return undefined;
    }
    return parseReferenceDataArray(rawJson['references']);
}

type RunActivityStartEntry = ActivityEntry & {
    readonly lifecycleKind: 'run-started';
};

type RunActivityTerminalEntry = ActivityEntry & {
    readonly lifecycleKind:
        | 'run-completed'
        | 'run-failed'
        | 'run-interrupted'
        | 'run-cancelled';
};

type RunActivityRequestedBoundaryEntry = ActivityEntry & {
    readonly lifecycleKind:
        | 'interrupt-requested'
        | 'run-cancel-requested'
        | 'stop-requested';
};

export function getLatestLiveAssistantMessageId({
    entries,
    liveEventIds,
    eventLayerReadEnabled,
}: {
    readonly entries: Array<TranscriptEntry>;
    readonly liveEventIds: ReadonlySet<string>;
    readonly eventLayerReadEnabled: boolean;
}): string | null {
    if (!eventLayerReadEnabled) {
        return null;
    }

    const latestLiveEntryIndex = getLatestLiveEntryIndex(
        entries,
        liveEventIds,
        (entry) => entry.kind === 'message' && entry.role === 'assistant',
    );
    if (latestLiveEntryIndex === null) {
        return null;
    }

    const latestUserMessageIndex = getLatestBlockingUserMessageIndex(
        entries,
        latestLiveEntryIndex,
    );
    let latestId: string | null = null;
    for (
        let index = latestUserMessageIndex + 1;
        index < entries.length;
        index += 1
    ) {
        const entry = entries[index];
        if (
            entry?.kind === 'message' &&
            entry.role === 'assistant' &&
            liveEventIds.has(entry.id)
        ) {
            latestId = entry.id;
        }
    }
    return latestId;
}

export function getLatestLiveThoughtId({
    entries,
    liveEventIds,
    eventLayerReadEnabled,
}: {
    readonly entries: Array<TranscriptEntry>;
    readonly liveEventIds: ReadonlySet<string>;
    readonly eventLayerReadEnabled: boolean;
}): string | null {
    if (!eventLayerReadEnabled) {
        return null;
    }

    const latestLiveEntryIndex = getLatestLiveEntryIndex(
        entries,
        liveEventIds,
        (entry) => entry.kind === 'thought',
    );
    if (latestLiveEntryIndex === null) {
        return null;
    }

    const latestUserMessageIndex = getLatestBlockingUserMessageIndex(
        entries,
        latestLiveEntryIndex,
    );
    let latestId: string | null = null;
    for (
        let index = latestUserMessageIndex + 1;
        index < entries.length;
        index += 1
    ) {
        const entry = entries[index];
        if (entry?.kind === 'thought' && liveEventIds.has(entry.id)) {
            latestId = entry.id;
        }
    }
    return latestId;
}

function getLatestLiveEntryIndex(
    entries: Array<TranscriptEntry>,
    liveEventIds: ReadonlySet<string>,
    matchesEntry: (entry: TranscriptEntry) => boolean,
): number | null {
    let latestLiveEntryIndex: number | null = null;
    for (let index = 0; index < entries.length; index += 1) {
        const entry = entries[index];
        if (entry && matchesEntry(entry) && liveEventIds.has(entry.id)) {
            latestLiveEntryIndex = index;
        }
    }
    return latestLiveEntryIndex;
}

function getLatestBlockingUserMessageIndex(
    entries: Array<TranscriptEntry>,
    latestLiveEntryIndex: number,
): number {
    for (let index = entries.length - 1; index >= 0; index -= 1) {
        const entry = entries[index];
        if (entry?.kind === 'message' && entry.role === 'user') {
            // Pending entries are appended after committed feed entries. If a
            // duplicate pending user row trails the current live response, it
            // should not suppress typewriter animation for that response.
            if (
                index > latestLiveEntryIndex &&
                entry.id.startsWith('pending-') &&
                isDuplicatePendingUserMessageForLiveEntry({
                    entries,
                    pendingEntry: entry,
                    latestLiveEntryIndex,
                }) &&
                !hasRunTerminalEntryBetween({
                    entries,
                    startIndex: latestLiveEntryIndex,
                    endIndex: index,
                })
            ) {
                continue;
            }
            return index;
        }
    }
    return -1;
}

function isDuplicatePendingUserMessageForLiveEntry({
    entries,
    pendingEntry,
    latestLiveEntryIndex,
}: {
    readonly entries: Array<TranscriptEntry>;
    readonly pendingEntry: MessageEntry;
    readonly latestLiveEntryIndex: number;
}): boolean {
    const normalizedContent = normalizeUserMessageText(pendingEntry.content);
    if (!normalizedContent) {
        return false;
    }

    const latestLiveEntry = entries[latestLiveEntryIndex];
    const pendingTimestampMs = getEventTimestampMs(pendingEntry.createdAt);
    const latestLiveTimestampMs = latestLiveEntry
        ? getEventTimestampMs(latestLiveEntry.createdAt)
        : null;
    if (
        pendingTimestampMs === null ||
        latestLiveTimestampMs === null ||
        pendingTimestampMs > latestLiveTimestampMs
    ) {
        return false;
    }

    for (let index = latestLiveEntryIndex - 1; index >= 0; index -= 1) {
        const entry = entries[index];
        if (!entry) {
            continue;
        }
        if (isRunActivityTerminalEntry(entry)) {
            return false;
        }
        if (
            entry.kind === 'message' &&
            entry.role === 'user' &&
            !entry.id.startsWith('pending-') &&
            normalizeUserMessageText(entry.content) === normalizedContent
        ) {
            return true;
        }
    }
    return false;
}

function hasRunTerminalEntryBetween({
    entries,
    startIndex,
    endIndex,
}: {
    readonly entries: Array<TranscriptEntry>;
    readonly startIndex: number;
    readonly endIndex: number;
}): boolean {
    for (let index = startIndex + 1; index < endIndex; index += 1) {
        const entry = entries[index];
        if (entry && isRunActivityTerminalEntry(entry)) {
            return true;
        }
    }
    return false;
}

type TimelineEntryForQueueCheck = {
    readonly kind: string;
    readonly id: string;
    readonly role?: string;
    readonly content?: string;
    readonly turnSequence?: number;
};

function isCurrentInlineOptimisticPendingEntry({
    entry,
    optimisticThinkingMarker,
}: {
    readonly entry: TimelineEntryForQueueCheck;
    readonly optimisticThinkingMarker: OptimisticThinkingMarker | null;
}): boolean {
    if (
        optimisticThinkingMarker === null ||
        optimisticThinkingMarker.displayMode !== 'inline'
    ) {
        return false;
    }
    if (
        entry.kind !== 'message' ||
        entry.role !== 'user' ||
        typeof entry.content !== 'string'
    ) {
        return false;
    }
    if (!entry.id.startsWith('pending-')) {
        return false;
    }
    return entry.content.trim() === optimisticThinkingMarker.text.trim();
}

export function isQueuedUserMessageTimelineEntry({
    entry,
    optimisticThinkingMarker,
    currentRunStartedOffset,
    hasPriorRunStarted,
}: {
    readonly entry: TimelineEntryForQueueCheck;
    readonly optimisticThinkingMarker: OptimisticThinkingMarker | null;
    readonly currentRunStartedOffset: number | null;
    readonly hasPriorRunStarted: boolean;
}): boolean {
    if (
        entry.kind !== 'message' ||
        entry.role !== 'user' ||
        typeof entry.content !== 'string'
    ) {
        return false;
    }

    // Idle-send optimistic message that arrived as a pending stream entry
    // stays inline rather than getting promoted to a queued pill.
    if (
        isCurrentInlineOptimisticPendingEntry({
            entry,
            optimisticThinkingMarker,
        })
    ) {
        return false;
    }

    if (
        currentRunStartedOffset !== null &&
        entry.turnSequence !== undefined &&
        entry.turnSequence > currentRunStartedOffset
    ) {
        return true;
    }

    // Pending stream entries (id `pending-…`) only count as queued when the
    // conversation already has a `run_started` event in the feed — i.e.
    // there is an existing run the new message is being queued behind. On
    // the very first message of a conversation there is no prior run, so a
    // pending entry for the current turn's own user message would otherwise
    // flash as a "queued" pill in the brief window between the optimistic
    // marker clearing (first assistant token) and the DB row upgrading from
    // pending → committed.
    return hasPriorRunStarted && entry.id.startsWith('pending-');
}

export type PhoebeFooterStatus = {
    readonly label: string;
    readonly detail?: string | null;
    readonly tone: 'idle' | 'working' | 'waiting' | 'error';
    readonly showSpinner: boolean;
    readonly canRetryRunError?: boolean;
    readonly canStopMonitoring?: boolean;
};

const DUPLICATE_MESSAGE_TIMESTAMP_WINDOW_MS = 2_000;

type TranscriptBuildEvent = {
    readonly id: string;
    readonly type: PhoebeAgentRunEventRead['type'];
    readonly agentItemId?: string | null;
    readonly payload: Record<string, unknown>;
    readonly createdAt: string;
    readonly turnSequence?: number;
    readonly commandId?: string | null;
    readonly causedByEventId?: string | null;
    readonly slackUserMentions?: Array<SlackUserMentionData>;
    readonly sentBy?: PhoebeAgentRunEventRead['sent_by'];
    readonly isPending?: boolean;
};

function getAgentTurnId(payload: Record<string, unknown>): string | undefined {
    return typeof payload['agent_turn_id'] === 'string'
        ? payload['agent_turn_id']
        : undefined;
}

type TranscriptBuildContext = {
    readonly entries: Array<TranscriptEntry>;
    readonly toolEntryIndexByCallId: Map<string, number>;
    readonly toolEntryIndexByCommandId: Map<string, number>;
    readonly slackThreadReplyTextByCallId: Map<string, string>;
    readonly slackThreadReplyMentionsByCallId: Map<
        string,
        Array<SlackUserMentionData>
    >;
    readonly pendingAssistantDestinationLinks: Array<MessageDestinationLink>;
    readonly projectedSlackReplyEntryIdsSinceLastUserMessage: Set<string>;
    readonly uploadedSlackArtifactIdsSinceLastUserMessage: Set<string>;
    readonly visibleAssistantArtifactIdsSinceLastUserMessage: Set<string>;
    readonly fanInState: FanInTranscriptState;
    readonly showCompactionEvents: boolean;
    readonly showInlineDebugEvents: boolean;
};

function invalidateDeniedApprovalNarration(
    context: TranscriptBuildContext,
    agentTurnId: string | undefined,
): void {
    if (!agentTurnId) {
        return;
    }
    const toolEntries = context.entries.filter(
        (entry): entry is ToolExecutionEntry =>
            entry.kind === 'tool-execution' &&
            entry.agentTurnId === agentTurnId &&
            entry.approvalState !== 'not_required' &&
            entry.approvalState !== null &&
            entry.approvalState !== undefined,
    );
    if (
        toolEntries.length === 0 ||
        !toolEntries.every((entry) => entry.approvalState === 'denied')
    ) {
        return;
    }
    for (let index = 0; index < context.entries.length; index += 1) {
        const entry = context.entries[index];
        if (
            entry?.kind === 'message' &&
            entry.role === 'assistant' &&
            entry.agentTurnId === agentTurnId
        ) {
            context.entries[index] = {
                ...entry,
                invalidatedByDeniedApproval: true,
            };
        }
    }
}

const SLACK_THREAD_REPLY_TOOL_NAME = 'post_to_slack_thread';
const ADMIN_SLACK_WRITE_TOOL_NAMES = new Set([
    'post_to_slack_channel',
    'reply_to_slack_thread',
    'upload_file_to_slack',
]);
const CURRENT_RUN_WEB_URL_TOOL_NAME = 'get_current_run_web_url';
const SLACK_NOTIFICATION_STATUS_SOURCES = new Set([
    'slack_notification',
    'slack_outreach_lifecycle',
]);
const TOOL_EXECUTION_LIFECYCLE_STATUSES = new Set([
    'announced',
    'running',
    'completed',
    'failed',
]);
const SUCCESSFUL_TOOL_OUTPUT_STATUSES = new Set([
    'completed',
    'success',
    'succeeded',
]);

function normalizeTranscriptMessageText(content: string): string {
    return content.trim().replace(/\s+/g, ' ');
}

function safeHttpUrl(value: unknown): string | null {
    if (typeof value !== 'string' || !value.trim()) {
        return null;
    }
    try {
        const url = new URL(value);
        return url.protocol === 'http:' || url.protocol === 'https:'
            ? url.toString()
            : null;
    } catch {
        return null;
    }
}

function uniqueDestinationLinks(
    links: ReadonlyArray<MessageDestinationLink>,
): Array<MessageDestinationLink> | undefined {
    const seenUrls = new Set<string>();
    const uniqueLinks: Array<MessageDestinationLink> = [];
    for (const link of links) {
        const key = link.url.toLowerCase();
        if (seenUrls.has(key)) {
            continue;
        }
        seenUrls.add(key);
        uniqueLinks.push(link);
    }
    return uniqueLinks.length > 0 ? uniqueLinks : undefined;
}

function appendDestinationLinkToLatestProjectedReply(
    context: TranscriptBuildContext,
    link: MessageDestinationLink,
): boolean {
    for (let index = context.entries.length - 1; index >= 0; index -= 1) {
        const entry = context.entries[index];
        if (entry?.kind !== 'message') {
            continue;
        }
        if (entry.role === 'user') {
            return false;
        }
        if (
            !context.projectedSlackReplyEntryIdsSinceLastUserMessage.has(
                entry.id,
            )
        ) {
            continue;
        }
        context.entries[index] = {
            ...entry,
            destinationLinks: uniqueDestinationLinks([
                ...(entry.destinationLinks ?? []),
                link,
            ]),
        };
        return true;
    }
    return false;
}

function getSlackDestinationLinkFromToolOutput(
    payload: Record<string, unknown>,
): MessageDestinationLink | null {
    const toolName = payload['tool_name'];
    if (typeof toolName !== 'string') {
        return null;
    }
    const isAdminSlackWrite = ADMIN_SLACK_WRITE_TOOL_NAMES.has(toolName);
    const isLegacySlackThreadReply = toolName === SLACK_THREAD_REPLY_TOOL_NAME;
    if (!isAdminSlackWrite && !isLegacySlackThreadReply) {
        return null;
    }
    if (
        isLegacySlackThreadReply
            ? !isSuccessfulToolOutput(payload)
            : !hasSuccessfulToolStatus(payload)
    ) {
        return null;
    }
    const outputJson = payload['output_json'];
    if (!isRecord(outputJson)) {
        return null;
    }
    const url = safeHttpUrl(outputJson['permalink']);
    return url ? { label: 'View in Slack', url } : null;
}

function getSlackThreadReplyTextFromToolCallPayload(
    payload: Record<string, unknown>,
): string | null {
    if (payload['tool_name'] !== SLACK_THREAD_REPLY_TOOL_NAME) {
        return null;
    }
    const argumentsJson = payload['arguments_json'];
    if (!isRecord(argumentsJson)) {
        return null;
    }
    const text = argumentsJson['text'];
    if (typeof text !== 'string') {
        return null;
    }
    const trimmedText = text.trim();
    return trimmedText.length > 0 ? trimmedText : null;
}

function getSlackThreadReplyTextFromToolOutput(
    outputJson: Record<string, unknown> | null,
): string | null {
    for (const key of ['text', 'assistant_text']) {
        const text = outputJson?.[key];
        if (typeof text !== 'string') {
            continue;
        }
        const trimmedText = text.trim();
        if (trimmedText.length > 0) {
            return trimmedText;
        }
    }
    return null;
}

function isSuccessfulSlackThreadReplyToolOutput(
    payload: Record<string, unknown>,
): boolean {
    if (payload['tool_name'] !== SLACK_THREAD_REPLY_TOOL_NAME) {
        return false;
    }
    return isSuccessfulToolOutput(payload);
}

function hasSuccessfulToolStatus(payload: Record<string, unknown>): boolean {
    const toolStatus = payload['tool_status'];
    return (
        typeof toolStatus === 'string' &&
        SUCCESSFUL_TOOL_OUTPUT_STATUSES.has(toolStatus.trim().toLowerCase())
    );
}

function isSuccessfulToolOutput(payload: Record<string, unknown>): boolean {
    if (!hasSuccessfulToolStatus(payload)) {
        return false;
    }
    const outputJson = payload['output_json'];
    return isRecord(outputJson) && outputJson['ok'] === true;
}

function getUploadedSlackArtifactIdFromToolOutput(
    payload: Record<string, unknown>,
): string | null {
    if (payload['tool_name'] !== 'upload_file_to_slack') {
        return null;
    }
    if (!hasSuccessfulToolStatus(payload)) {
        return null;
    }
    const outputJson = payload['output_json'];
    if (!isRecord(outputJson) || outputJson['status'] !== 'uploaded') {
        return null;
    }
    const artifactId = outputJson['uploaded_artifact_id'];
    return typeof artifactId === 'string' && artifactId.trim()
        ? artifactId.trim()
        : null;
}

function getCurrentRunWebUrlReplyTextFromToolOutputPayload(
    payload: Record<string, unknown>,
): string | null {
    if (payload['tool_name'] !== CURRENT_RUN_WEB_URL_TOOL_NAME) {
        return null;
    }
    if (!isSuccessfulToolOutput(payload)) {
        return null;
    }
    const outputJson = payload['output_json'];
    if (!isRecord(outputJson)) {
        return null;
    }
    const slackMrkdwn = outputJson['slack_mrkdwn'];
    if (typeof slackMrkdwn !== 'string') {
        return null;
    }
    const trimmedSlackMrkdwn = slackMrkdwn.trim();
    return trimmedSlackMrkdwn.length > 0 ? trimmedSlackMrkdwn : null;
}

function hasAssistantMessageWithNormalizedContentSinceLastUserMessage(
    context: TranscriptBuildContext,
    normalizedContent: string,
): boolean {
    for (let index = context.entries.length - 1; index >= 0; index -= 1) {
        const entry = context.entries[index];
        if (entry?.kind !== 'message') {
            continue;
        }
        if (entry.role === 'user') {
            return false;
        }
        if (
            normalizeTranscriptMessageText(entry.content) === normalizedContent
        ) {
            return true;
        }
    }
    return false;
}

function appendProjectedSlackThreadReply(
    context: TranscriptBuildContext,
    event: TranscriptBuildEvent,
    content: string,
    references?: Array<ReferenceData>,
    slackUserMentions?: Array<SlackUserMentionData>,
): void {
    const normalizedContent = normalizeTranscriptMessageText(content);
    if (!normalizedContent) {
        return;
    }
    if (
        hasAssistantMessageWithNormalizedContentSinceLastUserMessage(
            context,
            normalizedContent,
        )
    ) {
        return;
    }
    removeUnpostedAssistantTextEntriesSinceLastUserMessage(context);
    const entryId = `${event.id}:slack-thread-reply`;
    context.entries.push({
        kind: 'message',
        id: entryId,
        role: 'assistant',
        content,
        createdAt: event.createdAt,
        turnSequence: event.turnSequence,
        destinationLinks: uniqueDestinationLinks(
            context.pendingAssistantDestinationLinks,
        ),
        references,
        slackUserMentions,
    });
    context.pendingAssistantDestinationLinks.length = 0;
    context.projectedSlackReplyEntryIdsSinceLastUserMessage.add(entryId);
}

function removeUnpostedAssistantTextEntriesSinceLastUserMessage(
    context: TranscriptBuildContext,
): void {
    for (let index = context.entries.length - 1; index >= 0; index -= 1) {
        const entry = context.entries[index];
        if (entry?.kind !== 'message') {
            continue;
        }
        if (entry.role === 'user') {
            return;
        }
        if (
            context.projectedSlackReplyEntryIdsSinceLastUserMessage.has(
                entry.id,
            )
        ) {
            continue;
        }
        if (
            entry.role === 'assistant' &&
            entry.artifactRefs !== undefined &&
            entry.artifactRefs.some(
                (artifactRef) =>
                    !isUploadedSlackArtifactRef(context, artifactRef),
            )
        ) {
            continue;
        }
        context.entries.splice(index, 1);
        rebuildToolEntryIndexes(context);
    }
}

function getCommandId(
    event: Pick<TranscriptBuildEvent, 'payload' | 'commandId'>,
): string | null {
    if (event.commandId) {
        return event.commandId;
    }
    const payloadCommandId = event.payload['command_id'];
    return typeof payloadCommandId === 'string' ? payloadCommandId : null;
}

function getToolEntryIndex(
    context: TranscriptBuildContext,
    {
        callId,
        commandId,
        toolCallEventId,
    }: {
        readonly callId?: string | null;
        readonly commandId?: string | null;
        readonly toolCallEventId?: string | null;
    },
): number | undefined {
    if (callId) {
        const callIndex = context.toolEntryIndexByCallId.get(callId);
        if (callIndex !== undefined) {
            return callIndex;
        }
    }
    if (commandId) {
        const commandIndex = context.toolEntryIndexByCommandId.get(commandId);
        if (commandIndex !== undefined) {
            return commandIndex;
        }
    }
    if (toolCallEventId) {
        const eventIndex = context.entries.findIndex(
            (entry) =>
                entry.kind === 'tool-execution' &&
                entry.toolCallEventId === toolCallEventId,
        );
        return eventIndex >= 0 ? eventIndex : undefined;
    }
    return undefined;
}

function watchLifecycleLabel(kind: string | null): string {
    switch (kind) {
        case 'expired':
            return 'Watch expired';
        case 'suspended':
            return 'Watch suspended';
        case 'completed':
            return 'Watch completed';
        default:
            return 'Watch update';
    }
}

function shouldShowStatusUpdateInMainTranscript(
    payload: Record<string, unknown>,
): boolean {
    const source = payload['source'];
    if (typeof source !== 'string') {
        return false;
    }
    if (
        source === 'outreach_monitoring' &&
        payload['stage'] === 'monitoring_stopped'
    ) {
        return true;
    }
    return (
        SLACK_NOTIFICATION_STATUS_SOURCES.has(source) &&
        payload['stage'] === 'slack_posted'
    );
}

function registerToolEntry(
    context: TranscriptBuildContext,
    entryIndex: number,
    entry: ToolExecutionEntry,
) {
    if (entry.callId) {
        context.toolEntryIndexByCallId.set(entry.callId, entryIndex);
    }
    if (entry.commandId) {
        context.toolEntryIndexByCommandId.set(entry.commandId, entryIndex);
    }
}

function getEventSourcePendingEventId(
    event: PhoebeAgentRunEventRead,
): string | null {
    const eventWithPendingSource = event as PhoebeAgentRunEventRead & {
        readonly source_pending_event_id?: string | null;
    };
    return eventWithPendingSource.source_pending_event_id ?? null;
}

function getEventExternalRef(event: PhoebeAgentRunEventRead): string | null {
    const eventWithExternalRef = event as PhoebeAgentRunEventRead & {
        readonly external_ref?: string | null;
    };
    return eventWithExternalRef.external_ref ?? null;
}

const USER_MESSAGE_PENDING_RECONCILIATION_WINDOW_MS = 30 * 1000;

function getUserMessageText(payload: Record<string, unknown>): string | null {
    const text = String(payload['text'] ?? '').trim();
    const displayText = formatAgentTraceTextContent(text).trim();
    return displayText.length ? displayText : null;
}

function normalizeUserMessageText(text: string): string {
    return text.replace(/\s+/g, ' ').trim();
}

function getEventTimestampMs(timestamp: string): number | null {
    const timestampMs = Date.parse(timestamp);
    return Number.isFinite(timestampMs) ? timestampMs : null;
}

function getCommittedUserMessageTimestampMsByText(
    events: Array<PhoebeAgentRunEventRead>,
): Map<string, Array<number>> {
    const timestampMsByText = new Map<string, Array<number>>();

    for (const event of events) {
        if (event.type !== 'user_message') {
            continue;
        }

        const text = getUserMessageText(event.payload);
        const timestampMs = getEventTimestampMs(event.created_at);
        if (text === null || timestampMs === null) {
            continue;
        }

        const normalizedText = normalizeUserMessageText(text);
        const timestamps = timestampMsByText.get(normalizedText) ?? [];
        timestamps.push(timestampMs);
        timestampMsByText.set(normalizedText, timestamps);
    }

    return timestampMsByText;
}

function isPendingUserMessageAlreadyCommitted({
    pendingEvent,
    committedUserMessageTimestampMsByText,
}: {
    readonly pendingEvent: PhoebeAgentRunPendingEventRead;
    readonly committedUserMessageTimestampMsByText: Map<string, Array<number>>;
}): boolean {
    if (pendingEvent.type !== 'user_message') {
        return false;
    }

    const text = getUserMessageText(pendingEvent.payload);
    const pendingTimestampMs = getEventTimestampMs(pendingEvent.created_at);
    if (text === null || pendingTimestampMs === null) {
        return false;
    }

    const committedTimestampMsValues =
        committedUserMessageTimestampMsByText.get(
            normalizeUserMessageText(text),
        ) ?? [];

    return committedTimestampMsValues.some(
        (committedTimestampMs) =>
            pendingTimestampMs <= committedTimestampMs &&
            committedTimestampMs - pendingTimestampMs <=
                USER_MESSAGE_PENDING_RECONCILIATION_WINDOW_MS,
    );
}

function getToolExecutionLifecycleStatus(
    value: unknown,
): ToolExecutionLifecycleStatus | null {
    return typeof value === 'string' &&
        TOOL_EXECUTION_LIFECYCLE_STATUSES.has(value)
        ? (value as ToolExecutionLifecycleStatus)
        : null;
}

function getDebugEventToolName(
    payload: Record<string, unknown>,
): string | null {
    const toolName = payload['tool_name'];
    return typeof toolName === 'string' && toolName.trim()
        ? toolName.trim()
        : null;
}

function buildDebugEventEntryFromCommittedEvent(
    event: PhoebeAgentRunEventRead,
): DebugEventEntry {
    return {
        kind: 'debug-event',
        id: `debug-event-${event.id}`,
        createdAt: event.created_at,
        source: 'event',
        eventType: String(event.type),
        eventOffset: event.event_offset,
        status: null,
        toolName: getDebugEventToolName(event.payload),
        raw: event as unknown as Record<string, unknown>,
        turnSequence: event.event_offset,
    };
}

function buildDebugEventEntryFromPendingEvent(
    event: PhoebeAgentRunPendingEventRead,
): DebugEventEntry {
    return {
        kind: 'debug-event',
        id: `debug-pending-event-${event.id}`,
        createdAt: event.created_at,
        source: 'pending',
        eventType: String(event.type),
        eventOffset: null,
        status: typeof event.status === 'string' ? event.status : null,
        toolName: getDebugEventToolName(event.payload),
        raw: event as unknown as Record<string, unknown>,
    };
}

function insertDebugEventEntryByTimestamp(
    entries: Array<TranscriptEntry>,
    debugEntry: DebugEventEntry,
): void {
    const debugTimestampMs = getTranscriptEntryTimestampMs(debugEntry);
    if (debugTimestampMs === null) {
        entries.push(debugEntry);
        return;
    }

    const insertionIndex = entries.findIndex((entry) => {
        const entryTimestampMs = getTranscriptEntryTimestampMs(entry);
        return entryTimestampMs !== null && entryTimestampMs > debugTimestampMs;
    });
    if (insertionIndex === -1) {
        entries.push(debugEntry);
        return;
    }
    entries.splice(insertionIndex, 0, debugEntry);
}

function insertTranscriptEntryBlockByTimestamp(
    entries: Array<TranscriptEntry>,
    insertedEntries: Array<TranscriptEntry>,
    anchorCreatedAt: string,
): void {
    if (insertedEntries.length === 0) {
        return;
    }

    const anchorTimestampMs = Date.parse(anchorCreatedAt);
    if (!Number.isFinite(anchorTimestampMs)) {
        entries.push(...insertedEntries);
        return;
    }

    const insertionIndex = entries.findIndex((entry) => {
        const entryTimestampMs = getTranscriptEntryTimestampMs(entry);
        return (
            entryTimestampMs !== null && entryTimestampMs > anchorTimestampMs
        );
    });
    if (insertionIndex === -1) {
        entries.push(...insertedEntries);
        return;
    }
    entries.splice(insertionIndex, 0, ...insertedEntries);
}

function rebuildToolEntryIndexes(context: TranscriptBuildContext): void {
    context.toolEntryIndexByCallId.clear();
    context.toolEntryIndexByCommandId.clear();
    context.entries.forEach((entry, index) => {
        if (entry.kind === 'tool-execution') {
            registerToolEntry(context, index, entry);
        }
    });
}

function getToolCallLifecycleStatus(
    payload: Record<string, unknown>,
    status: string | null,
): ToolExecutionLifecycleStatus {
    const explicitLifecycle = getToolExecutionLifecycleStatus(
        payload['lifecycle_status'],
    );
    if (explicitLifecycle === 'announced' || explicitLifecycle === 'running') {
        return explicitLifecycle;
    }
    return status === 'pending_approval' || status === 'pending_user_input'
        ? 'announced'
        : 'running';
}

function getToolOutputLifecycleStatus(
    payload: Record<string, unknown>,
    error: string | null,
): ToolExecutionLifecycleStatus {
    const explicitLifecycle = getToolExecutionLifecycleStatus(
        payload['lifecycle_status'],
    );
    if (explicitLifecycle === 'completed' || explicitLifecycle === 'failed') {
        return explicitLifecycle;
    }
    const toolStatus =
        typeof payload['tool_status'] === 'string'
            ? payload['tool_status'].trim().toLowerCase()
            : '';
    return error ||
        toolStatus === 'failed' ||
        toolStatus === 'failure' ||
        toolStatus === 'incomplete' ||
        toolStatus === 'cancelled'
        ? 'failed'
        : 'completed';
}

function getArtifactRefKey(value: unknown): string | null {
    if (!isRecord(value)) {
        return null;
    }
    const artifactId = value['artifact_id'];
    return typeof artifactId === 'string' && artifactId.trim()
        ? artifactId
        : null;
}

function isUploadedSlackArtifactRef(
    context: TranscriptBuildContext,
    artifactRef: MessageArtifactRef,
): boolean {
    const artifactId = getArtifactRefKey(artifactRef);
    return (
        artifactId !== null &&
        context.uploadedSlackArtifactIdsSinceLastUserMessage.has(artifactId)
    );
}

function isVisibleAssistantArtifactRef(
    context: TranscriptBuildContext,
    artifactRef: MessageArtifactRef,
): boolean {
    const artifactId = getArtifactRefKey(artifactRef);
    return (
        artifactId !== null &&
        context.visibleAssistantArtifactIdsSinceLastUserMessage.has(artifactId)
    );
}

function removeUploadedArtifactRefsFromEntries(
    context: TranscriptBuildContext,
): void {
    for (let index = context.entries.length - 1; index >= 0; index -= 1) {
        const entry = context.entries[index];
        if (entry?.kind === 'message' && entry.role === 'user') {
            return;
        }
        if (
            entry?.kind !== 'message' ||
            entry.role !== 'assistant' ||
            entry.artifactRefs === undefined
        ) {
            continue;
        }
        const artifactRefs = entry.artifactRefs.filter(
            (artifactRef) => !isUploadedSlackArtifactRef(context, artifactRef),
        );
        if (artifactRefs.length === entry.artifactRefs.length) {
            continue;
        }
        if (artifactRefs.length === 0 && !entry.content) {
            context.entries.splice(index, 1);
            rebuildToolEntryIndexes(context);
            continue;
        }
        context.entries[index] = {
            ...entry,
            artifactRefs: artifactRefs.length > 0 ? artifactRefs : undefined,
        };
    }
}

function isMessageArtifactRef(value: unknown): value is MessageArtifactRef {
    // Admin artifact refs come from the durable artifact contracts and the
    // preview helpers that serialize them, both of which require a type and a
    // human-readable title. Keep this strict so artifact-only rows still have
    // stable labels when we render or fall back to chips.
    return (
        isRecord(value) &&
        typeof value['artifact_id'] === 'string' &&
        typeof value['artifact_type'] === 'string' &&
        typeof value['title'] === 'string'
    );
}

function collectArtifactRefsFromValue(
    value: unknown,
): Array<MessageArtifactRef> {
    const data = isRecord(value) ? value : {};
    const refs: Array<MessageArtifactRef> = [];
    if (
        isMessageArtifactRef(data['artifact_ref']) &&
        getArtifactRefKey(data['artifact_ref'])
    ) {
        refs.push(data['artifact_ref']);
    }
    for (const key of ['artifact_refs', 'artifacts']) {
        const artifactValues = data[key];
        if (!Array.isArray(artifactValues)) {
            continue;
        }
        refs.push(
            ...artifactValues.filter(
                (artifactValue): artifactValue is MessageArtifactRef =>
                    isMessageArtifactRef(artifactValue) &&
                    getArtifactRefKey(artifactValue) !== null,
            ),
        );
    }
    return refs;
}

function dedupeArtifactRefs(
    refs: ReadonlyArray<MessageArtifactRef>,
): Array<MessageArtifactRef> {
    const indexByKey = new Map<string, number>();
    const deduped: Array<MessageArtifactRef> = [];
    for (const ref of refs) {
        const key = getArtifactRefKey(ref);
        if (key === null) {
            continue;
        }
        const existingIndex = indexByKey.get(key);
        if (existingIndex !== undefined) {
            deduped[existingIndex] = ref;
            continue;
        }
        indexByKey.set(key, deduped.length);
        deduped.push(ref);
    }
    return deduped;
}

function mergeArtifactRefsIntoToolOutput(
    output: unknown,
    refs: ReadonlyArray<MessageArtifactRef>,
    options: { readonly preferOutputArtifactRefs?: boolean } = {},
): unknown {
    if (refs.length === 0) {
        return output;
    }
    // Non-record outputs (e.g. a bare string result) must survive the
    // merge — losing them would drop the tool's final payload from the
    // transcript when streamed artifact refs exist.
    const outputRecord = isRecord(output)
        ? output
        : output === null || output === undefined
          ? {}
          : { value: output };
    const existingArtifactRefs = collectArtifactRefsFromValue(outputRecord);
    const mergedRefs = options.preferOutputArtifactRefs
        ? dedupeArtifactRefs([...refs, ...existingArtifactRefs])
        : dedupeArtifactRefs([...existingArtifactRefs, ...refs]);
    return {
        ...outputRecord,
        artifacts: mergedRefs,
    };
}

function buildLifecycleEntry(
    event: TranscriptBuildEvent,
    {
        label,
        detail = null,
        phase = 'thinking',
        tone = 'default',
        canRetryRunError = false,
        lifecycleKind,
        visibleInMainTranscript = false,
        sourceEventType,
    }: {
        readonly label: string;
        readonly detail?: string | null;
        readonly phase?: LiveRunStatus['phase'];
        readonly tone?: ActivityEntry['tone'];
        readonly canRetryRunError?: boolean;
        readonly lifecycleKind?: ActivityEntry['lifecycleKind'];
        readonly visibleInMainTranscript?: boolean;
        readonly sourceEventType?: ActivityEntry['sourceEventType'];
    },
): ActivityEntry {
    return {
        kind: 'activity',
        id: event.id,
        createdAt: event.createdAt,
        phase,
        label,
        detail,
        toolName: null,
        tone,
        lifecycleKind,
        turnSequence: event.turnSequence,
        canRetryRunError,
        visibleInMainTranscript,
        sourceEventType,
    };
}

function stopRequestedLifecycleLabel(event: TranscriptBuildEvent): string {
    const displayName = event.sentBy?.display_name?.trim();
    return `${displayName && displayName.length > 0 ? displayName : 'You'} asked Phoebe to stop.`;
}

function isPrimaryFanInCompletionCallback(
    completionCallback: Record<string, unknown> | null,
): boolean {
    return (
        completionCallback?.['state'] === 'callback_drained' &&
        completionCallback['fan_in_role'] === 'primary_ready_signal'
    );
}

function isTerminalChildFailure(status: string): boolean {
    const normalizedStatus = status.trim().toLowerCase();
    return (
        normalizedStatus === 'failed' ||
        normalizedStatus === 'interrupted' ||
        normalizedStatus === 'cancelled'
    );
}

function recommendationFanoutReceipt(
    output: unknown,
): { readonly batchId: string; readonly candidateCount: number | null } | null {
    if (
        !isRecord(output) ||
        output['status'] !== 'recommendation_candidate_fanout_started'
    ) {
        return null;
    }
    const batchId = getOptionalString(output['batch_id']);
    if (!batchId) {
        return null;
    }
    const rawCount = output['candidate_count'];
    return {
        batchId,
        candidateCount:
            typeof rawCount === 'number' && Number.isFinite(rawCount)
                ? rawCount
                : null,
    };
}

// Fan-out receipts among completed tool calls that no recommendation-batch
// entry has claimed yet. This is a defensive fallback for event histories
// built before receipt-time carousel insertion.
function unclaimedRecommendationFanoutReceipts(
    entries: Array<TranscriptEntry>,
): Array<{ readonly batchId: string; readonly candidateCount: number | null }> {
    const claimedBatchIds = new Set(
        entries
            .filter((entry) => entry.kind === 'recommendation-batch')
            .map((entry) =>
                entry.kind === 'recommendation-batch' ? entry.batchId : '',
            ),
    );
    const receipts: Array<{
        readonly batchId: string;
        readonly candidateCount: number | null;
    }> = [];
    for (const entry of entries) {
        if (entry.kind !== 'tool-execution') {
            continue;
        }
        const receipt = recommendationFanoutReceipt(entry.output);
        if (receipt === null || claimedBatchIds.has(receipt.batchId)) {
            continue;
        }
        claimedBatchIds.add(receipt.batchId);
        receipts.push(receipt);
    }
    return receipts;
}

export function hasPendingRecommendationBatch(
    entries: Array<TranscriptEntry>,
): boolean {
    return entries.some(
        (entry) =>
            entry.kind === 'recommendation-batch' && !entry.aggregateArrived,
    );
}

function isStructuredSubagentTaskOutcomeText(content: string | null): boolean {
    if (content === null) {
        return false;
    }
    const trimmedContent = content.trim();
    if (!trimmedContent) {
        return false;
    }
    const unfencedContent = trimmedContent
        .replace(/^```(?:json)?\s*/iu, '')
        .replace(/\s*```$/u, '')
        .trim();
    if (!unfencedContent.startsWith('{')) {
        return false;
    }
    if (!/"task_outcome"\s*:/u.test(unfencedContent)) {
        return false;
    }
    try {
        const parsed = JSON.parse(unfencedContent) as unknown;
        if (!isRecord(parsed)) {
            return false;
        }
        return (
            typeof parsed['task_outcome'] === 'string' &&
            (typeof parsed['status'] === 'string' ||
                typeof parsed['reason_code'] === 'string' ||
                typeof parsed['follow_up_state'] === 'string' ||
                typeof parsed['summary'] === 'string')
        );
    } catch {
        return /"(status|reason_code|follow_up_state|summary)"\s*:/u.test(
            unfencedContent,
        );
    }
}

function isProgressRequest(content: string): boolean {
    return /\b(progress|status|update|how many|what(?:'|’)s back|what is back)\b/i.test(
        content,
    );
}

function isFanInProgressOnlyAssistantText(content: string): boolean {
    const normalizedContent = content.trim().replace(/\s+/g, ' ');
    if (!normalizedContent || normalizedContent.length > 260) {
        return false;
    }
    if (content.includes('\n') || content.includes('•')) {
        return false;
    }
    if (!/\b\d+\s+of\s+(?:the\s+)?\d+\b/i.test(normalizedContent)) {
        return false;
    }
    return /\b(received|have|got|seen)\b.*\b(shift|shifts|result|results|subagent|subagents|worker|workers)\b/i.test(
        normalizedContent,
    );
}

class FanInTranscriptState {
    primaryCallbacksSinceLastUserMessage = 0;
    latestUserExplicitlyRequestedProgress = false;

    resetForUserMessage(content: string): void {
        this.primaryCallbacksSinceLastUserMessage = 0;
        this.latestUserExplicitlyRequestedProgress = isProgressRequest(content);
    }

    notePrimaryCallback(): void {
        this.primaryCallbacksSinceLastUserMessage += 1;
    }

    shouldSuppressAssistantText(content: string): boolean {
        return (
            this.primaryCallbacksSinceLastUserMessage > 0 &&
            !this.latestUserExplicitlyRequestedProgress &&
            isFanInProgressOnlyAssistantText(content)
        );
    }

    noteVisibleAssistantText(): void {
        this.primaryCallbacksSinceLastUserMessage = 0;
    }
}

export function isRunActivityStartEntry(
    entry: TranscriptEntry,
): entry is RunActivityStartEntry {
    return entry.kind === 'activity' && entry.lifecycleKind === 'run-started';
}

export function isRunActivityTerminalEntry(
    entry: TranscriptEntry,
): entry is RunActivityTerminalEntry {
    return (
        entry.kind === 'activity' &&
        (entry.lifecycleKind === 'run-completed' ||
            entry.lifecycleKind === 'run-failed' ||
            entry.lifecycleKind === 'run-interrupted' ||
            entry.lifecycleKind === 'run-cancelled')
    );
}

export function isRunActivityRequestedBoundaryEntry(
    entry: TranscriptEntry,
): entry is RunActivityRequestedBoundaryEntry {
    return (
        entry.kind === 'activity' &&
        (entry.lifecycleKind === 'interrupt-requested' ||
            entry.lifecycleKind === 'run-cancel-requested' ||
            entry.lifecycleKind === 'stop-requested')
    );
}

export function shouldRenderStandaloneTerminalEntry(
    entry: RunActivityTerminalEntry,
): boolean {
    return entry.lifecycleKind !== 'run-completed';
}

export function isSyntheticCalloutKickoffMessage(content: string): boolean {
    const normalizedContent = content.trim();
    if (
        normalizedContent.startsWith(
            'Kick off analysis for this callout outreach.',
        ) &&
        normalizedContent.includes('\nOutreach ID:') &&
        (normalizedContent.includes('\nNumber of shifts:') ||
            normalizedContent.includes('\nShift IDs:') ||
            normalizedContent.includes(
                'temporary bootstrap flow using the general Phoebe agent',
            ))
    ) {
        return true;
    }

    return (
        (normalizedContent.startsWith('<outreach_kickoff>') &&
            normalizedContent.endsWith('</outreach_kickoff>')) ||
        (normalizedContent.startsWith('<outreach_monitor_wake_up>') &&
            normalizedContent.endsWith('</outreach_monitor_wake_up>')) ||
        (normalizedContent.startsWith('<automation_run_requested>') &&
            normalizedContent.endsWith('</automation_run_requested>'))
    );
}

type LegacySlackMessageInput = {
    readonly text: string;
    readonly authorLabel?: string;
};

export function isLegacySlackMessageXml(content: string): boolean {
    const normalizedContent = content.trim();
    return (
        normalizedContent.startsWith('<slack_message') &&
        normalizedContent.endsWith('</slack_message>')
    );
}

function decodeXmlTextContent(value: string): string {
    return value
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&apos;/g, "'")
        .replace(/&amp;/g, '&');
}

function getDirectChildTextContent(
    element: Element,
    childLocalName: string,
): string | null {
    for (const child of Array.from(element.children)) {
        if (child.localName !== childLocalName) {
            continue;
        }
        const content = child.textContent?.trim() ?? '';
        return content.length > 0 ? content : null;
    }
    return null;
}

function parseImessageMessageText(content: string): string | null {
    const normalizedContent = content.trim();
    if (
        !normalizedContent.startsWith('<imessage_message') ||
        !normalizedContent.endsWith('</imessage_message>')
    ) {
        return null;
    }

    const Parser = globalThis.DOMParser;
    if (Parser) {
        const document = new Parser().parseFromString(
            normalizedContent,
            'application/xml',
        );
        if (document.getElementsByTagName('parsererror').length === 0) {
            const root = document.documentElement;
            if (root.localName === 'imessage_message') {
                return getDirectChildTextContent(root, 'text');
            }
        }
    }

    const textMatch = normalizedContent.match(/<text>([\s\S]*?)<\/text>/);
    if (!textMatch?.[1]) {
        return null;
    }

    const text = decodeXmlTextContent(textMatch[1]).trim();
    return text.length > 0 ? text : null;
}

function formatImessageReactionSummary(
    description: string,
    targetText: string | null,
): string {
    return targetText
        ? `Reacted “${description}” to: “${targetText}”`
        : `Reacted “${description}” to an earlier message`;
}

function parseImessageReactionText(content: string): string | null {
    const normalizedContent = content.trim();
    if (
        !normalizedContent.startsWith('<imessage_reaction') ||
        !normalizedContent.endsWith('</imessage_reaction>')
    ) {
        return null;
    }

    const Parser = globalThis.DOMParser;
    if (Parser) {
        const document = new Parser().parseFromString(
            normalizedContent,
            'application/xml',
        );
        if (document.getElementsByTagName('parsererror').length === 0) {
            const root = document.documentElement;
            if (root.localName === 'imessage_reaction') {
                const reaction = getDirectChildElement(root, 'reaction');
                const description =
                    reaction?.getAttribute('custom_emoji')?.trim() ||
                    reaction?.getAttribute('type')?.trim() ||
                    'reaction';
                return formatImessageReactionSummary(
                    description,
                    getDirectChildTextContent(root, 'target_message'),
                );
            }
        }
    }

    const emojiMatch = normalizedContent.match(
        /<reaction\b[^>]*\bcustom_emoji="([^"]*)"/,
    );
    const typeMatch = normalizedContent.match(
        /<reaction\b[^>]*\btype="([^"]*)"/,
    );
    const description =
        decodeXmlTextContent(emojiMatch?.[1] || typeMatch?.[1] || '').trim() ||
        'reaction';
    const targetMatch = normalizedContent.match(
        /<target_message[^>]*>([\s\S]*?)<\/target_message>/,
    );
    const targetText = targetMatch?.[1]
        ? decodeXmlTextContent(targetMatch[1]).trim()
        : '';
    return formatImessageReactionSummary(
        description,
        targetText.length > 0 ? targetText : null,
    );
}

type UserMessageEnvelope = {
    readonly text: string;
    readonly from: string | null;
    readonly at: string | null;
};

export function parseUserMessageEnvelope(
    content: string,
): UserMessageEnvelope | null {
    const normalizedContent = content.trim();
    if (
        !normalizedContent.startsWith('<user_message') ||
        !normalizedContent.endsWith('</user_message>')
    ) {
        return null;
    }

    const Parser = globalThis.DOMParser;
    if (Parser) {
        const document = new Parser().parseFromString(
            normalizedContent,
            'application/xml',
        );
        if (document.getElementsByTagName('parsererror').length === 0) {
            const root = document.documentElement;
            if (root.localName === 'user_message') {
                return {
                    text: root.textContent ?? '',
                    from: root.getAttribute('from')?.trim() || null,
                    at: root.getAttribute('at')?.trim() || null,
                };
            }
        }
    }

    const bodyMatch = normalizedContent.match(
        /^<user_message[^>]*>([\s\S]*)<\/user_message>$/,
    );
    if (!bodyMatch) {
        return null;
    }
    const fromMatch = normalizedContent.match(/\bfrom="([^"]*)"/);
    const atMatch = normalizedContent.match(/\bat="([^"]*)"/);
    return {
        text: decodeXmlTextContent(bodyMatch[1] ?? ''),
        from: fromMatch?.[1] ? decodeXmlTextContent(fromMatch[1]).trim() : null,
        at: atMatch?.[1]?.trim() ?? null,
    };
}

export function formatAgentTraceTextContent(content: string): string {
    return (
        parseImessageMessageText(content) ??
        parseImessageReactionText(content) ??
        parseUserMessageEnvelope(content)?.text ??
        content
    );
}

function getDirectChildElement(
    element: Element,
    childLocalName: string,
): Element | null {
    for (const child of Array.from(element.children)) {
        if (child.localName === childLocalName) {
            return child;
        }
    }
    return null;
}

export function parseLegacySlackMessageInput(
    content: string,
): LegacySlackMessageInput | null {
    const normalizedContent = content.trim();
    if (!isLegacySlackMessageXml(normalizedContent)) {
        return null;
    }

    const Parser = globalThis.DOMParser;
    if (!Parser) {
        return null;
    }

    const document = new Parser().parseFromString(
        normalizedContent,
        'application/xml',
    );
    if (document.getElementsByTagName('parsererror').length > 0) {
        return null;
    }

    const root = document.documentElement;
    if (root.localName !== 'slack_message') {
        return null;
    }

    const text = getDirectChildTextContent(root, 'text');
    if (text === null) {
        return null;
    }

    const fromElement = getDirectChildElement(root, 'from');
    const displayName = fromElement?.textContent?.trim() ?? '';
    const slackUserId = fromElement?.getAttribute('slack_user_id')?.trim();
    const authorLabel =
        displayName.length > 0
            ? displayName
            : slackUserId
              ? `Slack user ${slackUserId}`
              : undefined;

    return { text, authorLabel };
}

export function isOptimisticThinkingHandoffEvent(
    event: PhoebeAgentRunEventRead,
): boolean {
    switch (event.type) {
        case 'assistant_text':
        case 'assistant_reasoning':
        case 'status_update':
        case 'tool_call':
        case 'tool_output':
        case 'artifact_updated':
        case 'run_completed':
        case 'run_failed':
        case 'run_cancelled':
            return true;
        case 'run_interrupted': {
            const payload =
                event.payload &&
                typeof event.payload === 'object' &&
                !Array.isArray(event.payload)
                    ? (event.payload as Record<string, unknown>)
                    : null;
            return payload?.['reason'] !== 'queued_user_message';
        }
        default:
            return false;
    }
}

// When a run terminates without producing a tool_output for an in-flight tool
// call (interrupt, cancel, fail), the tool entry stays isComplete=false and
// renders an indefinite spinner. Mark in-flight tools complete so the UI shows
// them as terminated instead of perpetually running.
function cancelInFlightToolEntries(
    context: TranscriptBuildContext,
    reason: string,
): void {
    for (let index = 0; index < context.entries.length; index += 1) {
        const entry = context.entries[index];
        if (
            entry?.kind !== 'tool-execution' ||
            entry.isComplete ||
            entry.status === 'pending_approval' ||
            entry.status === 'pending_user_input'
        ) {
            continue;
        }
        context.entries[index] = {
            ...entry,
            isComplete: true,
            status: 'cancelled',
            error: entry.error ?? reason,
            lifecycleStatus: 'failed',
        };
    }
}

function appendTranscriptEntriesFromPhoebeEvent(
    context: TranscriptBuildContext,
    event: TranscriptBuildEvent,
) {
    const payload = event.payload;

    switch (event.type) {
        case 'user_message': {
            // display_only echoes (e.g. a submitted question answer) exist
            // only as event-log bookkeeping — the model already excludes
            // them from its own context (event_projection.py), and each chat
            // renderer rebuilds the submitted answer from the completed tool
            // output so it can keep the original question beside it.
            if (payload['display_only'] === true) {
                break;
            }
            const content = formatAgentTraceTextContent(
                String(payload['text'] ?? ''),
            ).trim();
            const references = parseReferenceDataArray(payload['references']);
            const imageAttachments = parseMessageImageAttachments(
                payload['images'],
            );
            const uploadAttachments = parseMessageUploadAttachments(
                payload['uploads'],
            );
            const pendingDeliveryMode = parsePendingDeliveryMode(event);
            const widgetAction = parseDynamicWidgetActionRequest(
                payload['widget_action'],
            );
            const hiddenFromTranscript =
                payload['message_origin'] === 'chat_ui_action' ||
                widgetAction !== null;
            // A file-only user message must still render — the
            // attachment card carries the meaning even when the
            // coordinator sent no accompanying text. Dropping the
            // entry here would hide the upload from the transcript.
            if (!content && !imageAttachments && !uploadAttachments) {
                break;
            }
            const legacySlackMessage = parseLegacySlackMessageInput(content);
            if (legacySlackMessage !== null) {
                context.projectedSlackReplyEntryIdsSinceLastUserMessage.clear();
                context.uploadedSlackArtifactIdsSinceLastUserMessage.clear();
                context.visibleAssistantArtifactIdsSinceLastUserMessage.clear();
                context.pendingAssistantDestinationLinks.length = 0;
                context.fanInState.resetForUserMessage(legacySlackMessage.text);
                context.entries.push({
                    kind: 'message',
                    id: event.id,
                    role: 'user',
                    content: legacySlackMessage.text,
                    createdAt: event.createdAt,
                    turnSequence: event.turnSequence,
                    source: 'slack',
                    authorLabel: legacySlackMessage.authorLabel,
                    references,
                    imageAttachments,
                    pendingDeliveryMode,
                });
                break;
            }
            if (isLegacySlackMessageXml(content)) {
                break;
            }
            if (isSyntheticCalloutKickoffMessage(content)) {
                break;
            }
            context.projectedSlackReplyEntryIdsSinceLastUserMessage.clear();
            context.uploadedSlackArtifactIdsSinceLastUserMessage.clear();
            context.visibleAssistantArtifactIdsSinceLastUserMessage.clear();
            context.pendingAssistantDestinationLinks.length = 0;
            context.fanInState.resetForUserMessage(content);
            context.entries.push({
                kind: 'message',
                id: event.id,
                role: 'user',
                content:
                    content ||
                    _defaultUserMessageContent({
                        imageAttachments,
                        uploadAttachments,
                    }),
                createdAt: event.createdAt,
                turnSequence: event.turnSequence,
                agentTurnId: getAgentTurnId(payload),
                references,
                imageAttachments,
                uploadAttachments,
                sentBy: event.sentBy,
                pendingDeliveryMode,
                ...(widgetAction !== null ? { widgetAction } : {}),
                ...(hiddenFromTranscript ? { hiddenFromTranscript } : {}),
            });
            break;
        }
        case 'assistant_text': {
            const content = String(payload['text'] ?? '').trim();
            const artifactRefs = dedupeArtifactRefs(
                collectArtifactRefsFromValue(payload),
            ).filter(
                (artifactRef) =>
                    !isUploadedSlackArtifactRef(context, artifactRef) &&
                    !isVisibleAssistantArtifactRef(context, artifactRef),
            );
            const visibleContent =
                content &&
                context.projectedSlackReplyEntryIdsSinceLastUserMessage.size > 0
                    ? ''
                    : content;
            if (!visibleContent && artifactRefs.length === 0) {
                break;
            }
            for (const artifactRef of artifactRefs) {
                const artifactId = getArtifactRefKey(artifactRef);
                if (artifactId !== null) {
                    context.visibleAssistantArtifactIdsSinceLastUserMessage.add(
                        artifactId,
                    );
                }
            }
            if (
                visibleContent &&
                context.fanInState.shouldSuppressAssistantText(visibleContent)
            ) {
                break;
            }
            if (visibleContent) {
                context.fanInState.noteVisibleAssistantText();
            }
            const references = parseReferenceDataArray(payload['references']);
            const destinationLinks = uniqueDestinationLinks(
                context.pendingAssistantDestinationLinks,
            );
            context.entries.push({
                kind: 'message',
                id: event.id,
                role: 'assistant',
                content: visibleContent,
                createdAt: event.createdAt,
                turnSequence: event.turnSequence,
                agentTurnId: getAgentTurnId(payload),
                ...(event.agentItemId
                    ? { agentItemId: event.agentItemId }
                    : {}),
                destinationLinks,
                references,
                ...(artifactRefs.length > 0 ? { artifactRefs } : {}),
                slackUserMentions: event.slackUserMentions,
            });
            context.pendingAssistantDestinationLinks.length = 0;
            break;
        }
        case 'assistant_reasoning': {
            const content = String(payload['text'] ?? '').trim();
            if (!content) {
                break;
            }
            const references = parseReferenceDataArray(payload['references']);
            context.entries.push({
                kind: 'thought',
                id: event.id,
                createdAt: event.createdAt,
                content,
                tools: [],
                references,
                turnSequence: event.turnSequence,
                agentTurnId: getAgentTurnId(payload),
                ...(event.agentItemId
                    ? { agentItemId: event.agentItemId }
                    : {}),
            });
            break;
        }
        case 'tool_call': {
            const callId = String(payload['call_id'] ?? '');
            if (!callId) {
                break;
            }
            const slackThreadReplyText =
                getSlackThreadReplyTextFromToolCallPayload(payload);
            if (slackThreadReplyText !== null) {
                context.slackThreadReplyTextByCallId.set(
                    callId,
                    slackThreadReplyText,
                );
                if (event.slackUserMentions !== undefined) {
                    context.slackThreadReplyMentionsByCallId.set(
                        callId,
                        event.slackUserMentions,
                    );
                }
            }

            const commandId = getCommandId(event);
            const approvalState =
                payload['approval_state'] === 'not_required' ||
                payload['approval_state'] === 'pending' ||
                payload['approval_state'] === 'approved' ||
                payload['approval_state'] === 'denied'
                    ? payload['approval_state']
                    : null;
            const waitKind =
                payload['wait_kind'] === 'user_input' ||
                payload['wait_kind'] === 'approval' ||
                payload['wait_kind'] === 'none'
                    ? payload['wait_kind']
                    : null;
            const approvalGroup = parseToolApprovalGroup(
                payload['approval_group'],
            );
            const sourceWidgetActionId = getOptionalString(
                payload['source_widget_action_id'],
            );
            const toolStatus =
                payload['tool_status'] === 'pending_user_input'
                    ? 'pending_user_input'
                    : approvalState === 'pending'
                      ? 'pending_approval'
                      : null;
            const status =
                waitKind === 'user_input' ? 'pending_user_input' : toolStatus;
            const toolEntry: ToolExecutionEntry = {
                kind: 'tool-execution',
                id: event.id,
                toolCallEventId: event.id,
                createdAt: event.createdAt,
                name: String(payload['tool_name'] ?? 'Tool execution'),
                callId,
                commandId,
                status,
                approvalState,
                sourceWidgetActionId,
                arguments:
                    typeof payload['arguments_json'] === 'object' &&
                    payload['arguments_json'] !== null
                        ? (payload['arguments_json'] as
                              | Record<string, unknown>
                              | Array<unknown>)
                        : null,
                displayArguments: parseToolDisplayArguments(payload),
                references: parseReferenceDataArray(payload['references']),
                output: undefined,
                text: null,
                error: null,
                lifecycleStatus: getToolCallLifecycleStatus(payload, status),
                approvalGroup,
                isComplete: false,
                durationMs: null,
                agentTurnId: getAgentTurnId(payload),
                turnSequence: event.turnSequence,
            };
            const existingIndex = getToolEntryIndex(context, {
                callId,
                commandId,
            });
            if (existingIndex === undefined) {
                context.entries.push(toolEntry);
                registerToolEntry(
                    context,
                    context.entries.length - 1,
                    toolEntry,
                );
                break;
            }

            const existingEntry = context.entries[existingIndex];
            if (existingEntry?.kind !== 'tool-execution') {
                context.entries.push(toolEntry);
                registerToolEntry(
                    context,
                    context.entries.length - 1,
                    toolEntry,
                );
                break;
            }

            const mergedEntry: ToolExecutionEntry = {
                ...existingEntry,
                id: event.id,
                toolCallEventId: event.id,
                createdAt: existingEntry.createdAt,
                name: toolEntry.name,
                callId,
                commandId,
                status:
                    waitKind === 'user_input'
                        ? 'pending_user_input'
                        : approvalState === 'pending'
                          ? 'pending_approval'
                          : existingEntry.status,
                approvalState: approvalState ?? existingEntry.approvalState,
                sourceWidgetActionId:
                    sourceWidgetActionId ??
                    existingEntry.sourceWidgetActionId ??
                    null,
                arguments: toolEntry.arguments,
                displayArguments:
                    toolEntry.displayArguments ??
                    existingEntry.displayArguments,
                references: toolEntry.references ?? existingEntry.references,
                lifecycleStatus: existingEntry.isComplete
                    ? existingEntry.lifecycleStatus
                    : toolEntry.lifecycleStatus,
                approvalGroup:
                    toolEntry.approvalGroup ?? existingEntry.approvalGroup,
                turnSequence: existingEntry.turnSequence ?? event.turnSequence,
            };
            context.entries[existingIndex] = mergedEntry;
            registerToolEntry(context, existingIndex, mergedEntry);
            break;
        }
        case 'tool_output': {
            const callId = String(payload['call_id'] ?? '');
            const commandId = getCommandId(event);
            const errorType = payload['error_type'];
            // Coded rejections (`tool_call_error.*`) mean the tool refused
            // model-provided input; the model usually self-corrects on the
            // next turn, so they are deliberately rendered like a plain
            // completed call for end users. The output is dropped too: a
            // residual `{error}` / `{success: false}` payload would
            // re-trigger the review card via the output path. The raw
            // committed event keeps its error fields, so the debug timeline
            // and admin traces still see the failure.
            const isCodedToolCallRejection =
                typeof errorType === 'string' &&
                errorType.startsWith('tool_call_error.');
            const error = isCodedToolCallRejection
                ? null
                : typeof payload['error'] === 'string'
                  ? payload['error']
                  : null;
            const output = isCodedToolCallRejection
                ? undefined
                : (payload['output_text'] ?? payload['output_json']);
            const uploadedArtifactId =
                getUploadedSlackArtifactIdFromToolOutput(payload);
            if (uploadedArtifactId !== null) {
                context.uploadedSlackArtifactIdsSinceLastUserMessage.add(
                    uploadedArtifactId,
                );
                removeUploadedArtifactRefsFromEntries(context);
            }
            const nextEntry: ToolExecutionEntry = {
                kind: 'tool-execution',
                id: event.id,
                toolCallEventId: null,
                createdAt: event.createdAt,
                name: String(payload['tool_name'] ?? 'Tool execution'),
                callId: callId || null,
                commandId,
                status:
                    typeof payload['tool_status'] === 'string'
                        ? payload['tool_status']
                        : null,
                approvalState: null,
                sourceWidgetActionId: null,
                arguments: null,
                output,
                text: typeof output === 'string' ? output : null,
                error,
                lifecycleStatus: isCodedToolCallRejection
                    ? 'completed'
                    : getToolOutputLifecycleStatus(payload, error),
                isComplete: true,
                durationMs: null,
                turnSequence: event.turnSequence,
            };
            const existingIndex = getToolEntryIndex(context, {
                callId: callId || null,
                commandId,
            });
            if (existingIndex === undefined) {
                context.entries.push(nextEntry);
                registerToolEntry(
                    context,
                    context.entries.length - 1,
                    nextEntry,
                );
            } else {
                const existingEntry = context.entries[existingIndex];
                if (existingEntry?.kind !== 'tool-execution') {
                    context.entries.push(nextEntry);
                    registerToolEntry(
                        context,
                        context.entries.length - 1,
                        nextEntry,
                    );
                } else {
                    const mergedEntry: ToolExecutionEntry = {
                        ...existingEntry,
                        id: event.id,
                        // The output's call_id wins: rows matched via the
                        // command-id fallback may carry a null callId, and
                        // the call-id index must learn the real id here.
                        callId: nextEntry.callId ?? existingEntry.callId,
                        status: nextEntry.status,
                        output: mergeArtifactRefsIntoToolOutput(
                            nextEntry.output,
                            collectArtifactRefsFromValue(existingEntry.output),
                            { preferOutputArtifactRefs: true },
                        ),
                        text: nextEntry.text,
                        error: nextEntry.error,
                        lifecycleStatus: nextEntry.lifecycleStatus,
                        isComplete: true,
                    };
                    context.entries[existingIndex] = mergedEntry;
                    registerToolEntry(context, existingIndex, mergedEntry);
                }
            }
            const recommendationReceipt = recommendationFanoutReceipt(output);
            if (
                recommendationReceipt !== null &&
                !context.entries.some(
                    (entry) =>
                        entry.kind === 'recommendation-batch' &&
                        entry.batchId === recommendationReceipt.batchId,
                )
            ) {
                // The receipt is committed before the candidate forks run.
                // Surface the carousel now so coordinators watch decisions
                // arrive instead of first seeing it at the later fan-in pause.
                context.entries.push({
                    kind: 'recommendation-batch',
                    id: `${event.id}-recommendation-batch-${recommendationReceipt.batchId}`,
                    createdAt: event.createdAt,
                    batchId: recommendationReceipt.batchId,
                    candidateCount: recommendationReceipt.candidateCount,
                    aggregateArrived: false,
                    pauseEntryId: null,
                });
            }
            const slackThreadReplyText = callId
                ? context.slackThreadReplyTextByCallId.get(callId)
                : null;
            const destinationLink =
                getSlackDestinationLinkFromToolOutput(payload);
            if (destinationLink) {
                const didAttachToProjectedReply =
                    appendDestinationLinkToLatestProjectedReply(
                        context,
                        destinationLink,
                    );
                if (!didAttachToProjectedReply) {
                    context.pendingAssistantDestinationLinks.push(
                        destinationLink,
                    );
                }
            }
            if (
                slackThreadReplyText &&
                isSuccessfulSlackThreadReplyToolOutput(payload)
            ) {
                const outputJson = isRecord(payload['output_json'])
                    ? payload['output_json']
                    : null;
                const outputText =
                    getSlackThreadReplyTextFromToolOutput(outputJson);
                const projectedReplyText = outputText ?? slackThreadReplyText;
                const references = parseReferenceDataArray(
                    outputJson?.['references'],
                );
                const slackUserMentions =
                    event.slackUserMentions ??
                    (callId
                        ? context.slackThreadReplyMentionsByCallId.get(callId)
                        : undefined);
                appendProjectedSlackThreadReply(
                    context,
                    event,
                    projectedReplyText,
                    references,
                    slackUserMentions,
                );
            }
            const currentRunWebUrlReplyText =
                getCurrentRunWebUrlReplyTextFromToolOutputPayload(payload);
            if (currentRunWebUrlReplyText !== null) {
                appendProjectedSlackThreadReply(
                    context,
                    event,
                    currentRunWebUrlReplyText,
                );
            }
            break;
        }
        case 'artifact_updated': {
            const rawCallId = payload['call_id'] ?? payload['tool_call_id'];
            const callId =
                typeof rawCallId === 'string' && rawCallId.trim()
                    ? rawCallId
                    : null;
            const commandId = getCommandId(event);
            const artifactRefs = collectArtifactRefsFromValue(payload);
            if (artifactRefs.length === 0) {
                break;
            }

            const lifecycleStatus =
                getToolExecutionLifecycleStatus(payload['lifecycle_status']) ??
                'running';
            const existingIndex = getToolEntryIndex(context, {
                callId,
                commandId,
            });
            if (existingIndex === undefined) {
                const toolEntry: ToolExecutionEntry = {
                    kind: 'tool-execution',
                    id: event.id,
                    toolCallEventId: null,
                    createdAt: event.createdAt,
                    name: String(payload['tool_name'] ?? 'Tool execution'),
                    callId,
                    commandId,
                    status: null,
                    approvalState: null,
                    sourceWidgetActionId: null,
                    arguments: null,
                    output: mergeArtifactRefsIntoToolOutput(
                        undefined,
                        artifactRefs,
                    ),
                    text: null,
                    error: null,
                    lifecycleStatus,
                    isComplete: false,
                    durationMs: null,
                    turnSequence: event.turnSequence,
                };
                context.entries.push(toolEntry);
                registerToolEntry(
                    context,
                    context.entries.length - 1,
                    toolEntry,
                );
                break;
            }

            const existingEntry = context.entries[existingIndex];
            if (existingEntry?.kind !== 'tool-execution') {
                break;
            }

            const mergedEntry: ToolExecutionEntry = {
                ...existingEntry,
                commandId: commandId ?? existingEntry.commandId,
                output: mergeArtifactRefsIntoToolOutput(
                    existingEntry.output,
                    artifactRefs,
                ),
                lifecycleStatus: existingEntry.isComplete
                    ? existingEntry.lifecycleStatus
                    : lifecycleStatus,
            };
            context.entries[existingIndex] = mergedEntry;
            registerToolEntry(context, existingIndex, mergedEntry);
            break;
        }
        case 'tool_approval_required': {
            const presentation = parseToolApprovalPresentation(
                payload['presentation'],
            );
            const commandId = getCommandId(event);
            const existingIndex = getToolEntryIndex(context, {
                commandId,
                toolCallEventId: event.causedByEventId,
            });
            if (existingIndex === undefined) {
                break;
            }
            const existingEntry = context.entries[existingIndex];
            if (existingEntry?.kind !== 'tool-execution') {
                break;
            }
            const mergedEntry: ToolExecutionEntry = {
                ...existingEntry,
                commandId: commandId ?? existingEntry.commandId,
                ...(presentation === null
                    ? {}
                    : { approvalPresentation: presentation }),
            };
            context.entries[existingIndex] = mergedEntry;
            registerToolEntry(context, existingIndex, mergedEntry);
            break;
        }
        case 'user_question_required':
        case 'user_question_answered':
        case 'user_question_cancelled': {
            break;
        }
        case 'item_compaction': {
            if (!context.showCompactionEvents) {
                break;
            }
            const replacedItemCount = payload['replaced_item_count'];
            const summaryText = getOptionalString(payload['summary_text']);
            context.entries.push({
                kind: 'compaction-marker',
                id: event.id,
                createdAt: event.createdAt,
                replacedItemCount:
                    typeof replacedItemCount === 'number' &&
                    Number.isFinite(replacedItemCount)
                        ? replacedItemCount
                        : null,
                summaryText,
                turnSequence: event.turnSequence,
            });
            break;
        }
        case 'tool_approval_granted': {
            const commandId = getCommandId(event);
            const existingIndex = getToolEntryIndex(context, { commandId });
            if (existingIndex !== undefined) {
                const existingEntry = context.entries[existingIndex];
                if (existingEntry?.kind === 'tool-execution') {
                    const mergedEntry: ToolExecutionEntry = {
                        ...existingEntry,
                        commandId,
                        status: existingEntry.isComplete
                            ? existingEntry.status
                            : null,
                        approvalState: 'approved',
                        lifecycleStatus: existingEntry.isComplete
                            ? existingEntry.lifecycleStatus
                            : 'running',
                    };
                    context.entries[existingIndex] = mergedEntry;
                    registerToolEntry(context, existingIndex, mergedEntry);
                }
            }
            break;
        }
        case 'tool_approval_denied': {
            const commandId = getCommandId(event);
            const existingIndex = getToolEntryIndex(context, { commandId });
            if (existingIndex !== undefined) {
                const existingEntry = context.entries[existingIndex];
                if (existingEntry?.kind === 'tool-execution') {
                    const mergedEntry: ToolExecutionEntry = {
                        ...existingEntry,
                        commandId,
                        status: null,
                        approvalState: 'denied',
                        lifecycleStatus: 'failed',
                        agentTurnId: existingEntry.agentTurnId,
                    };
                    context.entries[existingIndex] = mergedEntry;
                    registerToolEntry(context, existingIndex, mergedEntry);
                    invalidateDeniedApprovalNarration(
                        context,
                        existingEntry.agentTurnId,
                    );
                }
            }
            break;
        }
        case 'run_started': {
            context.entries.push(
                buildLifecycleEntry(event, {
                    label: 'Working',
                    lifecycleKind: 'run-started',
                }),
            );
            break;
        }
        case 'run_completed': {
            context.entries.push(
                buildLifecycleEntry(event, {
                    label: 'Worked',
                    detail: getAdminAgentTierLifecycleDetail(payload),
                    tone: 'success',
                    lifecycleKind: 'run-completed',
                }),
            );
            break;
        }
        case 'run_failed': {
            cancelInFlightToolEntries(context, 'Run failed');
            context.entries.push(
                buildLifecycleEntry(event, {
                    label: 'Run failed',
                    detail:
                        typeof payload['error_message'] === 'string'
                            ? payload['error_message']
                            : null,
                    phase: 'responding',
                    tone: 'destructive',
                    canRetryRunError: true,
                    lifecycleKind: 'run-failed',
                }),
            );
            break;
        }
        case 'run_interrupted': {
            const reason =
                typeof payload['reason'] === 'string'
                    ? payload['reason']
                    : null;
            if (reason === 'subagent_fan_in_pending') {
                // A deliberate runtime pause while hidden evaluators finish
                // (recommendation fan-out), not a user interruption. Render
                // it as ordinary completed work, then surface the batch's
                // decision carousel as a standalone timeline entry so the
                // background progress stays visible after the work group
                // collapses.
                const pauseEntry = buildLifecycleEntry(event, {
                    label: 'Worked',
                    tone: 'success',
                    lifecycleKind: 'run-completed',
                });
                // Receipt-time entries are visible while the parent still
                // runs. Move pending ones after the pause boundary so, once
                // settled, the work group closes before the carousel and its
                // duration can extend through the final child completion.
                const pendingBatchEntries: Array<RecommendationBatchEntry> = [];
                for (
                    let index = context.entries.length - 1;
                    index >= 0;
                    index -= 1
                ) {
                    const entry = context.entries[index];
                    if (
                        entry?.kind !== 'recommendation-batch' ||
                        entry.aggregateArrived ||
                        entry.pauseEntryId !== null
                    ) {
                        continue;
                    }
                    pendingBatchEntries.unshift(entry);
                    context.entries.splice(index, 1);
                    rebuildToolEntryIndexes(context);
                }
                context.entries.push(pauseEntry);
                for (const batchEntry of pendingBatchEntries) {
                    context.entries.push({
                        ...batchEntry,
                        pauseEntryId: pauseEntry.id,
                    });
                }
                for (const receipt of unclaimedRecommendationFanoutReceipts(
                    context.entries,
                )) {
                    context.entries.push({
                        kind: 'recommendation-batch',
                        id: `${event.id}-recommendation-batch-${receipt.batchId}`,
                        createdAt: event.createdAt,
                        batchId: receipt.batchId,
                        candidateCount: receipt.candidateCount,
                        aggregateArrived: false,
                        pauseEntryId: pauseEntry.id,
                    });
                }
                break;
            }
            const approvalRejected = reason === 'tool_approval_denied';
            cancelInFlightToolEntries(
                context,
                approvalRejected ? 'Denied, not performed' : 'Interrupted',
            );
            context.entries.push(
                buildLifecycleEntry(event, {
                    label: approvalRejected
                        ? 'Denied, not performed'
                        : 'Interrupted',
                    detail: approvalRejected
                        ? 'You denied this, so nothing was sent or changed.'
                        : reason,
                    lifecycleKind: 'run-interrupted',
                }),
            );
            break;
        }
        case 'run_cancelled': {
            cancelInFlightToolEntries(context, 'Run cancelled');
            context.entries.push(
                buildLifecycleEntry(event, {
                    label: 'Run cancelled',
                    detail:
                        typeof payload['reason'] === 'string'
                            ? payload['reason']
                            : null,
                    lifecycleKind: 'run-cancelled',
                }),
            );
            break;
        }
        case 'child_completed': {
            const completionCallback = isRecord(payload['completion_callback'])
                ? payload['completion_callback']
                : null;
            if (completionCallback?.['state'] === 'already_consumed') {
                break;
            }
            const childRunId = getOptionalString(payload['child_run_id']);
            const terminalStatus =
                getOptionalString(payload['terminal_status']) ?? 'completed';
            const finalAssistantText = getOptionalString(
                payload['final_assistant_text'],
            );
            if (
                (isPrimaryFanInCompletionCallback(completionCallback) ||
                    isStructuredSubagentTaskOutcomeText(finalAssistantText)) &&
                !isTerminalChildFailure(terminalStatus)
            ) {
                context.fanInState.notePrimaryCallback();
                break;
            }
            const aggregate =
                parseRecommendationBatchAggregate(finalAssistantText);
            if (aggregate !== null) {
                const batchEntryIndex = context.entries.findIndex(
                    (existing) =>
                        existing.kind === 'recommendation-batch' &&
                        existing.batchId === aggregate.batchId,
                );
                const batchEntry = context.entries[batchEntryIndex];
                if (batchEntry?.kind === 'recommendation-batch') {
                    // The carousel already sits on the timeline from the
                    // fan-in pause; mark its batch settled instead of adding
                    // a duplicate raw aggregate marker.
                    context.entries[batchEntryIndex] = {
                        ...batchEntry,
                        aggregateArrived: true,
                        aggregateDecisions: aggregate.decisions,
                    };
                    if (batchEntry.pauseEntryId !== null) {
                        // The aggregate is written right after the last fork
                        // reaches terminal, so stretching the pause entry to
                        // its timestamp makes "Worked for" cover the whole
                        // fork window.
                        const pauseEntryIndex = context.entries.findIndex(
                            (existing) =>
                                existing.kind === 'activity' &&
                                existing.id === batchEntry.pauseEntryId,
                        );
                        const pauseEntry = context.entries[pauseEntryIndex];
                        if (pauseEntry?.kind === 'activity') {
                            context.entries[pauseEntryIndex] = {
                                ...pauseEntry,
                                createdAt: event.createdAt,
                            };
                        }
                    }
                    break;
                }
            }
            context.entries.push({
                kind: 'subagent-result',
                id: event.id,
                createdAt: event.createdAt,
                childRunId,
                terminalStatus,
                finalAssistantText,
                errorMessage:
                    getOptionalString(payload['error_message']) ??
                    getOptionalString(payload['interrupted_reason']),
                turnSequence: event.turnSequence,
            });
            break;
        }
        case 'interrupt_requested': {
            context.entries.push(
                buildLifecycleEntry(event, {
                    label: stopRequestedLifecycleLabel(event),
                    detail: null,
                    visibleInMainTranscript: true,
                    lifecycleKind: 'interrupt-requested',
                }),
            );
            break;
        }
        case 'run_cancel_requested': {
            context.entries.push(
                buildLifecycleEntry(event, {
                    label: 'Cancel requested',
                    detail:
                        typeof payload['reason'] === 'string'
                            ? payload['reason']
                            : null,
                    lifecycleKind: 'run-cancel-requested',
                }),
            );
            break;
        }
        case 'stop_requested': {
            const isMonitoringStop =
                payload['stop_kind'] === 'agent_monitoring';
            context.entries.push(
                buildLifecycleEntry(event, {
                    label: isMonitoringStop
                        ? 'Monitoring stopped'
                        : stopRequestedLifecycleLabel(event),
                    detail: null,
                    tone: isMonitoringStop ? 'success' : 'default',
                    visibleInMainTranscript: true,
                    lifecycleKind: isMonitoringStop
                        ? undefined
                        : 'stop-requested',
                }),
            );
            break;
        }
        case 'slack_thread_message': {
            const text = String(payload['text'] ?? '').trim();
            if (!text) {
                break;
            }
            const pendingDeliveryMode = parsePendingDeliveryMode(event);
            const displayName = payload['slack_user_display_name'];
            const slackUserId = payload['slack_user_id'];
            const authorLabel =
                typeof displayName === 'string' && displayName.length > 0
                    ? displayName
                    : typeof slackUserId === 'string'
                      ? `Slack user ${slackUserId}`
                      : undefined;
            context.projectedSlackReplyEntryIdsSinceLastUserMessage.clear();
            context.uploadedSlackArtifactIdsSinceLastUserMessage.clear();
            context.visibleAssistantArtifactIdsSinceLastUserMessage.clear();
            context.pendingAssistantDestinationLinks.length = 0;
            context.fanInState.resetForUserMessage(text);
            context.entries.push({
                kind: 'message',
                id: event.id,
                role: 'user',
                content: text,
                createdAt: event.createdAt,
                turnSequence: event.turnSequence,
                source: 'slack',
                authorLabel,
                slackUserMentions: event.slackUserMentions,
                pendingDeliveryMode,
            });
            break;
        }
        case 'poke': {
            // A poke that has not fired yet is surfaced by the composer chip,
            // not the transcript.
            if (event.isPending) {
                break;
            }
            context.entries.push(
                buildLifecycleEntry(event, {
                    label: 'Scheduled check-in',
                    detail: getOptionalString(payload['reason']),
                    sourceEventType: 'poke',
                }),
            );
            break;
        }
        case 'watch': {
            // Undrained fires are moments from draining; the live watch
            // itself is surfaced by the composer chip, not the transcript.
            if (event.isPending) {
                break;
            }
            context.entries.push(
                buildLifecycleEntry(event, {
                    label: watchLifecycleLabel(
                        getOptionalString(payload['kind']),
                    ),
                    detail: getOptionalString(payload['reason']),
                    sourceEventType: 'watch',
                }),
            );
            break;
        }
        case 'observed_outreach_updated':
        case 'outreach_kickoff':
        case 'outreach_monitor_wake_up':
        case 'automation_run_requested':
        case 'assistant_image':
        case 'user_image':
        case 'file_ref':
        case 'item_other':
            break;
        case 'status_update': {
            if (!shouldShowStatusUpdateInMainTranscript(payload)) {
                break;
            }
            const label =
                typeof payload['label'] === 'string' && payload['label'].trim()
                    ? payload['label'].trim()
                    : 'Phoebe notified Slack';
            const detail =
                typeof payload['detail'] === 'string' &&
                payload['detail'].trim()
                    ? payload['detail'].trim()
                    : null;
            context.entries.push(
                buildLifecycleEntry(event, {
                    label,
                    detail,
                    visibleInMainTranscript: true,
                }),
            );
            break;
        }
        default: {
            break;
        }
    }
}

export function getStringArray(value: unknown): Array<string> {
    return Array.isArray(value)
        ? value.filter((item): item is string => typeof item === 'string')
        : [];
}

export function getOptionalString(value: unknown): string | null {
    if (typeof value !== 'string') {
        return null;
    }
    const trimmed = value.trim();
    return trimmed ? trimmed : null;
}

function getAdminAgentTierLifecycleDetail(
    payload: Record<string, unknown>,
): string | null {
    if (getOptionalString(payload['admin_agent_tier']) !== 'auto') {
        return null;
    }
    const resolvedTier = getOptionalString(
        payload['admin_agent_resolved_tier'],
    );
    if (!resolvedTier) {
        return null;
    }
    const resolvedLabel =
        {
            instant: 'Instant',
            medium: 'Medium',
            high: 'High',
            extra_high: 'Extra High',
            pro: 'Pro',
        }[resolvedTier] ?? resolvedTier;
    const reason = getOptionalString(payload['admin_agent_tier_reason']);
    return reason
        ? `Auto -> ${resolvedLabel}: ${reason}`
        : `Auto -> ${resolvedLabel}`;
}

function getCompactionReplacedItemIds(
    event: PhoebeAgentRunEventRead,
): Array<string> {
    if (event.type !== 'item_compaction') {
        return [];
    }
    return getStringArray(event.payload['replaced_item_ids']);
}

type CompactionProjection<TNode, TAnchorKey> = {
    readonly hasCompactions: boolean;
    readonly filteredItemIds: ReadonlySet<string>;
    readonly liveCompactionItemIds: ReadonlySet<string>;
    readonly anchorKeyToCompactions: ReadonlyMap<
        TAnchorKey,
        ReadonlyArray<TNode>
    >;
    readonly replacedItemCountByCompactionItemId: ReadonlyMap<string, number>;
};

function buildCompactionProjection<TNode, TAnchorKey>({
    orderedNodes,
    isCompaction,
    getItemId,
    getReplacedItemIds,
    getAnchorKey,
}: {
    readonly orderedNodes: ReadonlyArray<TNode>;
    readonly isCompaction: (node: TNode) => boolean;
    readonly getItemId: (node: TNode) => string | null | undefined;
    readonly getReplacedItemIds: (node: TNode) => ReadonlyArray<string>;
    readonly getAnchorKey: (node: TNode, index: number) => TAnchorKey;
}): CompactionProjection<TNode, TAnchorKey> {
    const compactions: Array<{
        readonly itemId: string;
        readonly node: TNode;
    }> = [];
    const firstIndexByItemId = new Map<string, number>();
    const replacementEdges = new Map<string, Set<string>>();

    orderedNodes.forEach((node, index) => {
        const itemId = getItemId(node);
        if (itemId && !firstIndexByItemId.has(itemId)) {
            firstIndexByItemId.set(itemId, index);
        }
        if (itemId && isCompaction(node)) {
            compactions.push({ itemId, node });
            replacementEdges.set(itemId, new Set(getReplacedItemIds(node)));
        }
    });

    if (compactions.length === 0) {
        return {
            hasCompactions: false,
            filteredItemIds: new Set(),
            liveCompactionItemIds: new Set(),
            anchorKeyToCompactions: new Map(),
            replacedItemCountByCompactionItemId: new Map(),
        };
    }

    const allReplacedItemIds = new Set<string>();
    replacementEdges.forEach((replacedItemIds) => {
        replacedItemIds.forEach((itemId) => allReplacedItemIds.add(itemId));
    });

    const staleCompactionItemIds = new Set(
        [...replacementEdges.keys()].filter((itemId) =>
            allReplacedItemIds.has(itemId),
        ),
    );
    const liveCompactions = compactions.filter(
        ({ itemId }) => !allReplacedItemIds.has(itemId),
    );
    const transitiveCache = new Map<string, Set<string>>();
    const transitiveReplaces = (compactionItemId: string): Set<string> => {
        const cached = transitiveCache.get(compactionItemId);
        if (cached) {
            return cached;
        }

        const seen = new Set<string>();
        const stack = [...(replacementEdges.get(compactionItemId) ?? [])];
        while (stack.length > 0) {
            const itemId = stack.pop();
            if (!itemId || seen.has(itemId)) {
                continue;
            }
            seen.add(itemId);
            const nested = replacementEdges.get(itemId);
            if (nested) {
                nested.forEach((nestedItemId) => stack.push(nestedItemId));
            }
        }
        transitiveCache.set(compactionItemId, seen);
        return seen;
    };

    const filteredItemIds = new Set(staleCompactionItemIds);
    const anchorKeyToCompactions = new Map<TAnchorKey, Array<TNode>>();
    const replacedItemCountByCompactionItemId = new Map<string, number>();
    const orderForNode = (node: TNode): number => {
        const itemId = getItemId(node);
        return itemId ? (firstIndexByItemId.get(itemId) ?? -1) : -1;
    };

    liveCompactions.forEach(({ itemId, node }) => {
        const replacedItemIds = transitiveReplaces(itemId);
        replacedItemCountByCompactionItemId.set(itemId, replacedItemIds.size);
        replacedItemIds.forEach((replacedItemId) =>
            filteredItemIds.add(replacedItemId),
        );
        const anchorIndexes = [...replacedItemIds]
            .map((replacedItemId) => firstIndexByItemId.get(replacedItemId))
            .filter((index): index is number => index !== undefined);
        const physicalIndex = firstIndexByItemId.get(itemId);
        const anchorIndex =
            anchorIndexes.length > 0
                ? Math.min(...anchorIndexes)
                : (physicalIndex ?? 0);
        const anchorNode = orderedNodes[anchorIndex];
        if (anchorNode === undefined) {
            return;
        }
        const anchorKey = getAnchorKey(anchorNode, anchorIndex);
        const compactionsAtAnchor = anchorKeyToCompactions.get(anchorKey) ?? [];
        compactionsAtAnchor.push(node);
        compactionsAtAnchor.sort(
            (left, right) => orderForNode(left) - orderForNode(right),
        );
        anchorKeyToCompactions.set(anchorKey, compactionsAtAnchor);
    });

    return {
        hasCompactions: true,
        filteredItemIds,
        liveCompactionItemIds: new Set(
            liveCompactions.map(({ itemId }) => itemId),
        ),
        anchorKeyToCompactions,
        replacedItemCountByCompactionItemId,
    };
}

function projectCompactedPhoebeEvents(
    events: Array<PhoebeAgentRunEventRead>,
): Array<PhoebeAgentRunEventRead> {
    const projection = buildCompactionProjection({
        orderedNodes: events,
        isCompaction: (event) => event.type === 'item_compaction',
        getItemId: (event) => event.agent_item_id,
        getReplacedItemIds: getCompactionReplacedItemIds,
        getAnchorKey: (_event, index) => index,
    });
    if (!projection.hasCompactions) {
        return events;
    }

    const projected: Array<PhoebeAgentRunEventRead> = [];
    const emittedCompactionItemIds = new Set<string>();
    const skippedEventIds = new Set<string>();

    events.forEach((event, index) => {
        const anchoredCompactions =
            projection.anchorKeyToCompactions.get(index) ?? [];
        for (const anchoredCompaction of anchoredCompactions) {
            if (
                anchoredCompaction.agent_item_id &&
                !emittedCompactionItemIds.has(anchoredCompaction.agent_item_id)
            ) {
                projected.push(anchoredCompaction);
                emittedCompactionItemIds.add(anchoredCompaction.agent_item_id);
            }
        }

        if (
            event.caused_by_event_id &&
            skippedEventIds.has(event.caused_by_event_id)
        ) {
            skippedEventIds.add(event.id);
            return;
        }

        if (
            event.agent_item_id &&
            projection.filteredItemIds.has(event.agent_item_id)
        ) {
            skippedEventIds.add(event.id);
            return;
        }

        if (
            event.agent_item_id &&
            projection.liveCompactionItemIds.has(event.agent_item_id)
        ) {
            if (emittedCompactionItemIds.has(event.agent_item_id)) {
                return;
            }
            projected.push(event);
            emittedCompactionItemIds.add(event.agent_item_id);
            return;
        }

        projected.push(event);
    });

    return projected;
}

export type LegacyOrderedItem = {
    readonly item: ToolAwareItem;
    readonly turnSequence: number;
};

export type LegacyCompactionProjection = {
    readonly filteredItemIds: ReadonlySet<string>;
    readonly liveCompactionItemIds: ReadonlySet<string>;
    readonly anchorItemIdToCompactions: ReadonlyMap<
        string,
        ReadonlyArray<LegacyOrderedItem>
    >;
    readonly replacedItemCountByCompactionItemId: ReadonlyMap<string, number>;
};

export function projectCompactedLegacyItems(
    orderedItems: Array<LegacyOrderedItem>,
): LegacyCompactionProjection {
    const projection = buildCompactionProjection({
        orderedNodes: orderedItems,
        isCompaction: ({ item }) => item.type === 'compaction',
        getItemId: ({ item }) => item.id,
        getReplacedItemIds: ({ item }) =>
            getStringArray(item.replaces_item_ids),
        getAnchorKey: ({ item }) => item.id,
    });

    return {
        filteredItemIds: projection.filteredItemIds,
        liveCompactionItemIds: projection.liveCompactionItemIds,
        anchorItemIdToCompactions: projection.anchorKeyToCompactions,
        replacedItemCountByCompactionItemId:
            projection.replacedItemCountByCompactionItemId,
    };
}

export function buildTranscriptFromPhoebeEvents(
    events: Array<PhoebeAgentRunEventRead>,
    pendingEvents: Array<PhoebeAgentRunPendingEventRead> = [],
    {
        showCompactionEvents = false,
        showInlineDebugEvents = false,
        runId = null,
    }: {
        readonly showCompactionEvents?: boolean;
        readonly showInlineDebugEvents?: boolean;
        readonly runId?: string | null;
    } = {},
): Array<TranscriptEntry> {
    const projectedEvents = showCompactionEvents
        ? projectCompactedPhoebeEvents(events)
        : events;
    const context: TranscriptBuildContext = {
        entries: [],
        toolEntryIndexByCallId: new Map<string, number>(),
        toolEntryIndexByCommandId: new Map<string, number>(),
        slackThreadReplyTextByCallId: new Map<string, string>(),
        slackThreadReplyMentionsByCallId: new Map<
            string,
            Array<SlackUserMentionData>
        >(),
        pendingAssistantDestinationLinks: [],
        projectedSlackReplyEntryIdsSinceLastUserMessage: new Set<string>(),
        uploadedSlackArtifactIdsSinceLastUserMessage: new Set<string>(),
        visibleAssistantArtifactIdsSinceLastUserMessage: new Set<string>(),
        fanInState: new FanInTranscriptState(),
        showCompactionEvents,
        showInlineDebugEvents,
    };

    for (const event of projectedEvents) {
        appendTranscriptEntriesFromPhoebeEvent(context, {
            id: event.id,
            type: event.type,
            agentItemId: event.agent_item_id,
            payload: event.payload,
            createdAt: event.created_at,
            turnSequence: event.event_offset,
            commandId: event.command_id,
            causedByEventId: event.caused_by_event_id,
            slackUserMentions: getSlackUserMentionsFromEvent(event),
            sentBy: event.sent_by,
        });
        if (context.showInlineDebugEvents) {
            context.entries.push(buildDebugEventEntryFromCommittedEvent(event));
        }
    }

    const committedSourcePendingEventIds = new Set(
        events
            .map((event) => getEventSourcePendingEventId(event))
            .filter(
                (pendingEventId): pendingEventId is string =>
                    pendingEventId !== null,
            ),
    );
    const committedExternalRefs = new Set(
        events
            .map((event) => getEventExternalRef(event))
            .filter(
                (externalRef): externalRef is string => externalRef !== null,
            ),
    );
    const committedUserMessageTimestampMsByText =
        getCommittedUserMessageTimestampMsByText(events);

    for (const pendingEvent of pendingEvents) {
        const isRepresentedByCommittedEvent =
            committedSourcePendingEventIds.has(pendingEvent.id) ||
            (pendingEvent.external_ref !== null &&
                committedExternalRefs.has(pendingEvent.external_ref)) ||
            isPendingUserMessageAlreadyCommitted({
                pendingEvent,
                committedUserMessageTimestampMsByText,
            });
        const shouldShowPendingDebugEvent =
            context.showInlineDebugEvents &&
            pendingEvent.status !== 'drained' &&
            !isRepresentedByCommittedEvent;
        if (
            pendingEvent.status !== 'pending' ||
            isRepresentedByCommittedEvent
        ) {
            if (shouldShowPendingDebugEvent) {
                insertDebugEventEntryByTimestamp(
                    context.entries,
                    buildDebugEventEntryFromPendingEvent(pendingEvent),
                );
                rebuildToolEntryIndexes(context);
            }
            continue;
        }

        const pendingBuildEvent = {
            id: `pending-${pendingEvent.id}`,
            type: pendingEvent.type,
            payload: pendingEvent.payload,
            createdAt: pendingEvent.created_at,
            commandId:
                typeof pendingEvent.payload['command_id'] === 'string'
                    ? pendingEvent.payload['command_id']
                    : null,
            isPending: true,
        };
        if (!shouldShowPendingDebugEvent) {
            appendTranscriptEntriesFromPhoebeEvent(context, pendingBuildEvent);
            continue;
        }

        const appendedEntryStartIndex = context.entries.length;
        appendTranscriptEntriesFromPhoebeEvent(context, pendingBuildEvent);
        const pendingTranscriptEntries = context.entries.splice(
            appendedEntryStartIndex,
        );
        insertTranscriptEntryBlockByTimestamp(
            context.entries,
            [
                ...pendingTranscriptEntries,
                buildDebugEventEntryFromPendingEvent(pendingEvent),
            ],
            pendingEvent.created_at,
        );
        rebuildToolEntryIndexes(context);
    }

    // While a fan-out batch is still evaluating, drop its pause terminal
    // entry so the work group stays open and its clock keeps running. The
    // aggregate's arrival restores the entry (with a stretched timestamp),
    // closing the group at "worked for <run start → last fork done>".
    const pendingPauseEntryIds = new Set(
        context.entries
            .filter(
                (entry) =>
                    entry.kind === 'recommendation-batch' &&
                    !entry.aggregateArrived &&
                    entry.pauseEntryId !== null,
            )
            .map((entry) =>
                entry.kind === 'recommendation-batch'
                    ? entry.pauseEntryId
                    : null,
            ),
    );
    if (pendingPauseEntryIds.size > 0) {
        return attachRunIdToToolEntries(
            context.entries.filter(
                (entry) =>
                    !(
                        entry.kind === 'activity' &&
                        pendingPauseEntryIds.has(entry.id)
                    ),
            ),
            runId,
        );
    }

    return attachRunIdToToolEntries(context.entries, runId);
}

function attachRunIdToToolEntries(
    entries: ReadonlyArray<TranscriptEntry>,
    runId: string | null,
): Array<TranscriptEntry> {
    if (!isValidAgentRunId(runId)) {
        return [...entries];
    }
    return entries.map((entry) => {
        if (entry.kind === 'tool-execution') {
            return { ...entry, runId };
        }
        if (entry.kind === 'thought' && entry.tools.length > 0) {
            return {
                ...entry,
                tools: entry.tools.map((tool) => ({ ...tool, runId })),
            };
        }
        return entry;
    });
}

function getTranscriptEntryTimestampMs(entry: TranscriptEntry): number | null {
    const timestampMs = Date.parse(entry.createdAt);
    return Number.isFinite(timestampMs) ? timestampMs : null;
}

function getEventFeedStartTimestampMs({
    events,
    pendingEvents,
}: {
    readonly events: Array<PhoebeAgentRunEventRead>;
    readonly pendingEvents: Array<PhoebeAgentRunPendingEventRead>;
}): number | null {
    const timestampValues = [...events, ...pendingEvents]
        .map((event) => {
            const timestampMs = Date.parse(event.created_at);
            return Number.isFinite(timestampMs) ? timestampMs : null;
        })
        .filter((timestampMs): timestampMs is number => timestampMs !== null);

    return timestampValues.length ? Math.min(...timestampValues) : null;
}

function getEventBackedAgentItemIds(
    events: Array<PhoebeAgentRunEventRead>,
): ReadonlySet<string> {
    return new Set(
        events
            .map((event) => event.agent_item_id)
            .filter((agentItemId): agentItemId is string =>
                Boolean(agentItemId),
            ),
    );
}

function getEventBackedToolCallIds(
    events: Array<PhoebeAgentRunEventRead>,
): ReadonlySet<string> {
    return new Set(
        events
            .filter(
                (event) =>
                    event.type === 'tool_call' || event.type === 'tool_output',
            )
            .map((event) => event.payload['call_id'])
            .filter((callId): callId is string => typeof callId === 'string'),
    );
}

function mergeTranscriptEntriesByTimestamp(
    eventTranscript: Array<TranscriptEntry>,
    legacyTail: Array<TranscriptEntry>,
): Array<TranscriptEntry> {
    return [
        ...eventTranscript.map((entry, index) => ({ entry, index })),
        ...legacyTail.map((entry, index) => ({
            entry,
            index: eventTranscript.length + index,
        })),
    ]
        .sort((left, right) => {
            const leftTimestampMs = getTranscriptEntryTimestampMs(left.entry);
            const rightTimestampMs = getTranscriptEntryTimestampMs(right.entry);
            if (
                leftTimestampMs !== null &&
                rightTimestampMs !== null &&
                leftTimestampMs !== rightTimestampMs
            ) {
                return leftTimestampMs - rightTimestampMs;
            }

            return left.index - right.index;
        })
        .map(({ entry }) => entry);
}

export function mergeLegacyTailAfterEventFeedStart({
    eventTranscript,
    legacyTranscript,
    events,
    pendingEvents,
}: {
    readonly eventTranscript: Array<TranscriptEntry>;
    readonly legacyTranscript: Array<TranscriptEntry>;
    readonly events: Array<PhoebeAgentRunEventRead>;
    readonly pendingEvents: Array<PhoebeAgentRunPendingEventRead>;
}): Array<TranscriptEntry> {
    if (!legacyTranscript.length) {
        return eventTranscript;
    }

    const eventFeedStartTimestampMs = getEventFeedStartTimestampMs({
        events,
        pendingEvents,
    });
    if (eventFeedStartTimestampMs === null) {
        return legacyTranscript;
    }

    const eventMessages = eventTranscript
        .filter((entry): entry is MessageEntry => entry.kind === 'message')
        .map((entry) => ({
            role: entry.role,
            content: entry.content.trim(),
            timestampMs: getTranscriptEntryTimestampMs(entry),
        }));
    const eventBackedAgentItemIds = getEventBackedAgentItemIds(events);
    const eventBackedToolCallIds = getEventBackedToolCallIds(events);
    const legacyTail = legacyTranscript.filter((entry) => {
        const timestampMs = getTranscriptEntryTimestampMs(entry);
        if (timestampMs === null || timestampMs <= eventFeedStartTimestampMs) {
            return false;
        }

        if (eventBackedAgentItemIds.has(entry.id)) {
            return false;
        }

        if (
            entry.kind === 'tool-execution' &&
            entry.callId !== null &&
            eventBackedToolCallIds.has(entry.callId)
        ) {
            return false;
        }

        if (
            entry.kind === 'message' &&
            eventMessages.some(
                (eventMessage) =>
                    eventMessage.role === entry.role &&
                    eventMessage.content === entry.content.trim() &&
                    eventMessage.timestampMs !== null &&
                    Math.abs(eventMessage.timestampMs - timestampMs) <=
                        DUPLICATE_MESSAGE_TIMESTAMP_WINDOW_MS,
            )
        ) {
            return false;
        }

        return true;
    });

    return legacyTail.length
        ? mergeTranscriptEntriesByTimestamp(eventTranscript, legacyTail)
        : eventTranscript;
}
